import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ChatMessage from '../components/ChatMessage';
import DocViewerPanel from '../components/DocViewerPanel';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Cpu, ArrowLeft, Send, Coins, Check, Zap, MessageSquare, Star, Heart, Mic, MicOff, Square,
  ShieldCheck, Headphones, TrendingUp, Users, FileText, Download, Paperclip, X, File,
  BarChart3, DollarSign, Stethoscope, FileCheck, Shield, ClipboardCheck,
  Scale, BookOpen, Wrench, Truck, Home, Leaf, Megaphone, Calculator, Loader2, Image, Code, Pencil,
  Bot, Search, Globe, Eye, EyeOff, Presentation, Database, Trash2, Upload,
  Plus, Camera, ImagePlus, Sparkles, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  GitBranch, RotateCcw, FolderOpen, Calendar, Github, MoreVertical
} from 'lucide-react';


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  ShieldCheck, Headphones, TrendingUp, Users, FileText, BarChart3,
  DollarSign, Stethoscope, FileCheck, Shield, ClipboardCheck, Scale,
  BookOpen, Wrench, Truck, Home, Zap, Leaf, Megaphone, Calculator, Code, Presentation,
  Bot, Search, Globe, GitBranch
};

const ALLOWED_EXTENSIONS = ['.pdf', '.txt', '.csv', '.json', '.md', '.doc', '.docx', '.xls', '.xlsx', '.png', '.jpg', '.jpeg', '.gif', '.webp'];
const CHAT_INPUT_MAX_HEIGHT = 160;

const TIER_HIERARCHY = {
  'free': 0,
  'pro': 1,
  'enterprise': 2,
  'enterprise_pro': 3
};

const formatAgentName = (name = '') => String(name ?? '');

// ─────────────────────────────────────────────────────────────────────────────
//  Response accessor helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Extract the inner data block from an axios response. */
const _axiosData = (response) => response?.data?.data ?? {};

/**
 * Extract data from a synthetic SSE response object.
 */
const _sseData = (response) => response?.data ?? {};

const _stripAttachmentMarkers = (text = '') => String(text)
  .replace(/\s*\[Files attached:[^\]]*\]/gi, '')
  .replace(/\s*\[Google Drive files attached:[^\]]*\]/gi, '')
  .trim();

const _attachmentName = (file = {}) =>
  file.original_filename || file.filename || file.name || file.stored_filename || file.id || 'File';

const _isImageAttachment = (name = '') => /\.(png|jpe?g|gif|webp|bmp|tiff?)$/i.test(name);

const _extractMessageAttachments = (msg) => {
  if (Array.isArray(msg?.attachments) && msg.attachments.length > 0) return msg.attachments;
  if (Array.isArray(msg?.user_uploaded_docs) && msg.user_uploaded_docs.length > 0) return msg.user_uploaded_docs;
  if (Array.isArray(msg?.files) && msg.files.length > 0) {
    return msg.files.map((f) => ({
      id: f.id,
      source: f.source || 'local_upload',
      original_filename: f.original_filename || f.filename || f.name,
      file_type: f.file_type,
    }));
  }
  return [];
};


/* ── Memoized message list — only re-renders when messages/sending change ── */
const MemoizedMessageList = React.memo(({
  messages,
  sending,
  streamEvents,
  parsePptDownloadToken,
  downloadGeneratedPpt,
  downloadGeneratedDoc,
  downloadGeneratedZip,
  docViewerDataMap,
  docViewerFileId,
  setDocViewerFileId,
  setDocViewerFilename,
  setDocViewerOpen,
  setDocViewerLoading,
  setDocViewerDataMap,
  token,
  messagesEndRef,
  isDark,
  onImageClick
}) => {
  return (
    <div className="space-y-4 px-2 max-w-3xl mx-auto w-full">
      {messages.map((msg, i) => {
        const parsed = msg.role === 'assistant'
          ? parsePptDownloadToken(msg.content)
          : { cleanContent: _stripAttachmentMarkers(msg.content), fileId: null, filename: null, fileType: null };

        const messageAttachments = msg.role === 'user' ? _extractMessageAttachments(msg) : [];

        const isUser = msg.role === 'user';
        const timeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        return (
          <div
            key={msg._streamId || i}
            className={`chat-message flex gap-2.5 ${isUser ? 'justify-end' : 'justify-start'}`}
          >
            {/* Agent avatar */}
            {!isUser && (
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#F87217] to-[#FF4F00] flex items-center justify-center shadow-sm flex-shrink-0 mt-1">
                <Bot className="w-3.5 h-3.5 text-white" />
              </div>
            )}

            <div className={`flex flex-col min-w-0 ${isUser ? 'max-w-[78%] items-end' : 'max-w-[92%] w-full items-start'}`}>
              <div
                className={`px-4 py-3 shadow-sm ${isUser
                  ? 'bg-gradient-to-br from-[#FF4F00] to-[#F87217] text-white rounded-2xl rounded-tr-sm'
                  : `border ${isDark ? 'bg-[#0f1729] border-[#1e2d52] text-[#c9d1d9]' : 'bg-white border-slate-200/80 text-slate-900'} rounded-2xl rounded-tl-sm w-full overflow-x-auto`
                  }`}
                data-testid={`chat-message-${i}`}
                style={isUser ? { boxShadow: '0 2px 12px rgba(248,114,23,0.25)' } : { boxShadow: '0 1px 8px rgba(0,0,0,0.06)' }}
              >
                <ChatMessage content={parsed.cleanContent} role={msg.role} onImageClick={onImageClick} />
                {/* Blinking cursor while streaming */}
                {msg._streamId && (
                  <span className="inline-block w-2 h-4 bg-slate-400 animate-pulse ml-0.5 align-text-bottom" />
                )}
                {msg.role === 'assistant' && Array.isArray(msg.streamEvents) && msg.streamEvents.length > 0 && (
                  <div className="mt-3 pt-2 border-t border-slate-200">
                    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">
                      Generation Steps
                    </p>
                    <div className="space-y-1">
                      {msg.streamEvents.slice(-8).map((evt, idx) => (
                        <div key={`${evt.timestamp || idx}-${idx}`} className="text-xs text-slate-600">
                          <span className="font-medium">{evt.stage || 'step'}:</span> {evt.message}
                          {evt.tool ? ` (${evt.tool})` : ''}
                          {typeof evt.score === 'number' ? ` score=${evt.score}` : ''}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {isUser && messageAttachments.length > 0 && (
                <div className="mt-1.5 flex flex-wrap justify-end gap-1.5 w-full">
                  {messageAttachments.map((file, idx) => {
                    const displayName = _attachmentName(file);
                    return (
                      <div
                        key={`${file.id || displayName}-${idx}`}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] max-w-[240px] ${isDark ? 'bg-[#0f1729] border-[#1e2d52] text-[#c9d1d9]' : 'bg-slate-100 border-slate-200 text-slate-700'}`}
                        title={displayName}
                      >
                        {String(file.source || '').toLowerCase() === 'google_drive' ? (
                          <img src="https://upload.wikimedia.org/wikipedia/commons/1/12/Google_Drive_icon_%282020%29.svg" alt="" className="w-3.5 h-3.5" />
                        ) : _isImageAttachment(displayName) ? (
                          <Image className="w-3.5 h-3.5" />
                        ) : (
                          <File className="w-3.5 h-3.5" />
                        )}
                        <span className="truncate">{displayName}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Download / View buttons */}
              {msg.role === 'assistant' && parsed.fileId && parsed.fileType === 'ppt' && (
                <div className="mt-2 flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => downloadGeneratedPpt(parsed.fileId, parsed.filename)}
                    className="bg-[#F87217] hover:bg-[#C65B12] text-white rounded-full text-xs px-4"
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" />
                    Download Presentation
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadGeneratedPpt(parsed.fileId, parsed.filename, true)}
                    className="rounded-full text-xs px-4"
                  >
                    <Pencil className="w-3.5 h-3.5 mr-1.5" />
                    Rename &amp; Download
                  </Button>
                </div>
              )}
              {msg.role === 'assistant' && parsed.fileId && parsed.fileType === 'doc' && (
                <div className="mt-2 flex gap-2 flex-wrap">
                  <Button
                    size="sm"
                    onClick={async () => {
                      setDocViewerFileId(parsed.fileId);
                      setDocViewerFilename(parsed.filename);
                      setDocViewerOpen(true);
                      if (!docViewerDataMap[parsed.fileId]) {
                        setDocViewerLoading(true);
                        try {
                          const res = await axios.get(`${API}/doc/${parsed.fileId}/data`, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          setDocViewerDataMap(prev => ({ ...prev, [parsed.fileId]: res.data.doc_data }));
                        } catch (err) {
                          console.error('Failed to fetch doc data:', err);
                          toast.error(err.response?.data?.detail || 'Could not load document data');
                          setDocViewerOpen(false);
                        } finally {
                          setDocViewerLoading(false);
                        }
                      }
                    }}
                    className="bg-[#F87217] hover:bg-[#C65B12] text-white rounded-full text-xs px-4"
                  >
                    <Eye className="w-3.5 h-3.5 mr-1.5" />
                    View Document
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => downloadGeneratedDoc(parsed.fileId, parsed.filename)} className="rounded-full text-xs px-4">
                    <Download className="w-3.5 h-3.5 mr-1.5" />Download
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => downloadGeneratedDoc(parsed.fileId, parsed.filename, true)} className="rounded-full text-xs px-4">
                    <Pencil className="w-3.5 h-3.5 mr-1.5" />Rename &amp; Download
                  </Button>
                </div>
              )}
              {msg.role === 'assistant' && parsed.fileId && parsed.fileType === 'zip' && (
                <div className="mt-2 flex gap-2 flex-wrap">
                  <Button
                    size="sm"
                    onClick={() => downloadGeneratedZip(parsed.fileId, parsed.filename)}
                    className="bg-[#F87217] hover:bg-[#C65B12] text-white rounded-full text-xs px-4"
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" />
                    Download ZIP
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => downloadGeneratedZip(parsed.fileId, parsed.filename, true)} className="rounded-full text-xs px-4">
                    <Pencil className="w-3.5 h-3.5 mr-1.5" />Rename &amp; Download
                  </Button>
                </div>
              )}

              {/* Timestamp */}
              <span className="text-[10px] text-slate-400 mt-1 px-1">{timeStr}</span>
            </div>

            {/* User avatar */}
            {
              isUser && (
                <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center shadow-sm flex-shrink-0 mt-1">
                  <Users className="w-3.5 h-3.5 text-slate-500" />
                </div>
              )
            }
          </div>
        );
      })}
      {
        sending && (
          <div className="flex justify-start gap-2.5">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#F87217] to-[#FF4F00] flex items-center justify-center shadow-sm flex-shrink-0 mt-1">
              <Bot className="w-3.5 h-3.5 text-white" />
            </div>
            <div className={`border px-4 py-3 rounded-2xl rounded-tl-sm min-w-[160px] ${isDark ? 'bg-[#0f1729] border-[#1e2d52]' : 'bg-white border-slate-200/80'}`} style={{ boxShadow: '0 1px 8px rgba(0,0,0,0.06)' }}>
              {/* Typing indicator */}
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              {streamEvents && streamEvents.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    Agent Progress
                  </p>
                  {streamEvents.slice(-6).map((evt, idx) => (
                    <div key={`${evt.timestamp || idx}-${idx}`} className="text-xs text-slate-600">
                      <span className="font-medium">{evt.stage || 'step'}:</span> {evt.message}
                      {evt.tool ? ` (${evt.tool})` : ''}
                      {typeof evt.score === 'number' ? ` score=${evt.score}` : ''}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )
      }
      <div ref={messagesEndRef} />
    </div >
  );
});
MemoizedMessageList.displayName = 'MemoizedMessageList';

const AgentDetail = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, token, updateCredits } = useAuth();
  const { isDark } = useTheme();

  useEffect(() => {
    if (agentId === 'real-estate') {
      navigate('/property-ai', { replace: true });
    }
  }, [agentId, navigate]);

  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [streamEvents, setStreamEvents] = useState([]);

  // Doc viewer panel state
  const [docViewerOpen, setDocViewerOpen] = useState(false);
  const [docViewerDataMap, setDocViewerDataMap] = useState({});
  const [docViewerFileId, setDocViewerFileId] = useState(null);
  const [docViewerFilename, setDocViewerFilename] = useState(null);
  const [docSaving, setDocSaving] = useState(false);
  const [docViewerLoading, setDocViewerLoading] = useState(false);

  // ── NEW: Fullscreen wireframe modal state ───────────────────────────────
  const [expandedImage, setExpandedImage] = useState(null);

  // ── Input bar collapse state ──────────────────────────────────────────
  const [inputBarCollapsed, setInputBarCollapsed] = useState(false);

  // ── NEW: sidebar reviews collapse state ───────────────────────────────────
  const [sidebarReviewsOpen, setSidebarReviewsOpen] = useState(false);

  // ── Sidebar collapsed/expanded state ─────────────────────────────────────
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Mobile header menu state
  const [showMobileHeaderMenu, setShowMobileHeaderMenu] = useState(false);

  // ── REMOVED: activeTab (reviews moved to sidebar) ─────────────────────────
  const [isChatFullscreen, setIsChatFullscreen] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [togglingFavorite, setTogglingFavorite] = useState(false);

  // File upload states
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Attach menu
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const attachMenuRef = useRef(null);

  // Tool chips
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  const [deepResearchEnabled, setDeepResearchEnabled] = useState(false);
  const [imageGenEnabled, setImageGenEnabled] = useState(false);

  // Connectors
  const [showConnectorsMenu, setShowConnectorsMenu] = useState(false);
  const connectorsMenuRef = useRef(null);
  const [connectedApps, setConnectedApps] = useState({ drive: false, calendar: false, github: false });
  const [connectingApp, setConnectingApp] = useState(null);

  // Drive file picker state
  const [showDrivePicker, setShowDrivePicker] = useState(false);
  const [driveFiles, setDriveFiles] = useState([]);
  const [driveSearch, setDriveSearch] = useState('');
  const [driveLoading, setDriveLoading] = useState(false);
  const [selectedDriveFiles, setSelectedDriveFiles] = useState([]);

  // Fetch connector status on mount & listen for OAuth callback params
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    // Check URL for OAuth completion redirect
    const params = new URLSearchParams(window.location.search);
    const connectorParam = params.get('connector');
    if (connectorParam) {
      const mapping = { gdrive_connected: 'drive', gcalendar_connected: 'calendar', github_connected: 'github' };
      const key = mapping[connectorParam];
      if (key) {
        setConnectedApps(prev => ({ ...prev, [key]: true }));
        toast.success(`${key === 'drive' ? 'Google Drive' : key === 'calendar' ? 'Google Calendar' : 'GitHub'} connected!`);
        // Clean URL
        const url = new URL(window.location);
        url.searchParams.delete('connector');
        window.history.replaceState({}, '', url);
      }
    }
    // Fetch current connector status
    axios.get(`${API}/connectors/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const active = res.data?.connectors || [];
        setConnectedApps({
          drive: active.includes('google_drive'),
          calendar: active.includes('google_calendar'),
          github: active.includes('github'),
        });
      }).catch(() => { });
  }, [isAuthenticated, token]);

  // Fetch Drive files when picker opens or search changes
  useEffect(() => {
    if (!showDrivePicker || !connectedApps.drive || !token) return;
    const fetchDriveFiles = async () => {
      setDriveLoading(true);
      try {
        const params = driveSearch ? `?q=${encodeURIComponent(driveSearch)}` : '';
        const res = await axios.get(`${API}/connectors/gdrive/files${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDriveFiles(res.data?.files || []);
      } catch (e) {
        console.error('Drive files fetch failed', e);
      }
      setDriveLoading(false);
    };
    const debounce = setTimeout(fetchDriveFiles, driveSearch ? 400 : 0);
    return () => clearTimeout(debounce);
  }, [showDrivePicker, driveSearch, connectedApps.drive, token]);

  const toggleConnector = async (key, label) => {
    // If already connected → disconnect
    if (connectedApps[key]) {
      const endpointMap = { drive: 'gdrive', calendar: 'gcalendar', github: 'github' };
      try {
        await axios.post(`${API}/connectors/${endpointMap[key]}/disconnect`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setConnectedApps(prev => ({ ...prev, [key]: false }));
        if (key === 'drive') { setSelectedDriveFiles([]); setShowDrivePicker(false); }
        toast.success(`${label} disconnected`);
      } catch (e) {
        toast.error(`Failed to disconnect ${label}`);
      }
      return;
    }
    // Connect → start OAuth flow in a popup
    const oauthMap = { drive: 'gdrive', calendar: 'gcalendar', github: 'github' };
    setConnectingApp(key);

    let oauthUrl = '';
    try {
      const start = await axios.get(`${API}/connectors/oauth/${oauthMap[key]}/start`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      oauthUrl = start.data?.oauth_url || '';
      if (!oauthUrl) {
        throw new Error('No OAuth URL returned by server');
      }
    } catch (e) {
      setConnectingApp(null);
      toast.error(`Failed to connect ${label}`);
      return;
    }

    const popup = window.open(oauthUrl, `connect_${key}`, 'width=600,height=700');

    // Listen for postMessage from the OAuth popup
    const connectorKeyMap = { gdrive_connected: 'drive', gcalendar_connected: 'calendar', github_connected: 'github' };
    const onMessage = async (event) => {
      if (event.origin !== window.location.origin && event.origin !== new URL(API).origin) return;
      if (event.data?.type === 'oauth_complete') {
        window.removeEventListener('message', onMessage);
        clearInterval(pollTimer);
        setConnectingApp(null);
        const connectedKey = connectorKeyMap[event.data.connector] || key;
        setConnectedApps(prev => ({ ...prev, [connectedKey]: true }));
        toast.success(`${label} connected!`);
        if (popup && !popup.closed) popup.close();
      }
    };
    window.addEventListener('message', onMessage);

    // Fallback: poll for popup close in case postMessage doesn't fire
    const pollTimer = setInterval(async () => {
      if (!popup || popup.closed) {
        clearInterval(pollTimer);
        window.removeEventListener('message', onMessage);
        setConnectingApp(null);
        // Re-check connector status
        try {
          const res = await axios.get(`${API}/connectors/status`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const active = res.data?.connectors || [];
          const wasConnected = {
            drive: active.includes('google_drive'),
            calendar: active.includes('google_calendar'),
            github: active.includes('github'),
          };
          setConnectedApps(wasConnected);
          if (wasConnected[key]) toast.success(`${label} connected!`);
        } catch (e) { /* ignore */ }
      }
    }, 1000);
  };

  const toggleDriveFile = (file) => {
    setSelectedDriveFiles(prev => {
      const exists = prev.find(f => f.id === file.id);
      if (exists) return prev.filter(f => f.id !== file.id);
      if (prev.length >= 5) { toast.error('Maximum 5 Drive files'); return prev; }
      return [...prev, file];
    });
  };

  // ── KB Drive import ─────────────────────────────────────────────────────
  const [showKbDrivePicker, setShowKbDrivePicker] = useState(false);
  const [kbDriveFiles, setKbDriveFiles] = useState([]);
  const [kbDriveSearch, setKbDriveSearch] = useState('');
  const [kbDriveLoading, setKbDriveLoading] = useState(false);
  const [kbDriveImporting, setKbDriveImporting] = useState(null);

  useEffect(() => {
    if (!showKbDrivePicker || !connectedApps.drive || !token) return;
    const fetchKbDriveFiles = async () => {
      setKbDriveLoading(true);
      try {
        const params = kbDriveSearch ? `?q=${encodeURIComponent(kbDriveSearch)}` : '';
        const res = await axios.get(`${API}/connectors/gdrive/files${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setKbDriveFiles(res.data?.files || []);
      } catch (e) { console.error('KB Drive files fetch failed', e); }
      setKbDriveLoading(false);
    };
    const debounce = setTimeout(fetchKbDriveFiles, kbDriveSearch ? 400 : 0);
    return () => clearTimeout(debounce);
  }, [showKbDrivePicker, kbDriveSearch, connectedApps.drive, token]);

  const importDriveFileToKb = async (file) => {
    if (!kbCategory && !kbConnected) {
      toast.error('Select a KB category first');
      return;
    }
    const category = kbCategory || 'General';
    setKbDriveImporting(file.id);
    try {
      await axios.post(`${API}/connectors/gdrive/import-to-kb`, {
        file_id: file.id,
        category,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`"${file.name}" imported to KB (${category}) — indexing...`);
      // Refresh KB categories to update file counts
      const catRes = await axios.get(`${API}/kb/categories`, { headers: { Authorization: `Bearer ${token}` } });
      setKbCategories(Array.isArray(catRes.data) ? catRes.data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to import from Drive');
    }
    setKbDriveImporting(null);
  };

  const getDriveFileIcon = (mimeType) => {
    if (!mimeType) return <FileText className="w-3.5 h-3.5 text-slate-500" />;
    if (mimeType.includes('image/')) return <Image className="w-3.5 h-3.5 text-green-500" />;
    if (mimeType.includes('pdf')) return <FileText className="w-3.5 h-3.5 text-red-500" />;
    if (mimeType.includes('document') || mimeType.includes('word')) return <FileText className="w-3.5 h-3.5 text-blue-500" />;
    return <FileText className="w-3.5 h-3.5 text-slate-500" />;
  };

  // Voice recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  // Review states
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: '', comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [userHasReviewed, setUserHasReviewed] = useState(false);
  const [togglingVisibility, setTogglingVisibility] = useState(false);
  const [confirmDeleteAgent, setConfirmDeleteAgent] = useState(false);
  const [deletingAgent, setDeletingAgent] = useState(false);

  // Knowledge Base connection state
  const [kbConnected, setKbConnected] = useState(false);
  const [kbCategory, setKbCategory] = useState('');
  const [kbCategories, setKbCategories] = useState([]);
  const [showKbDropdown, setShowKbDropdown] = useState(false);
  const kbDropdownRef = useRef(null);

  // ── Database Agent state ──────────────────────────────────────────────────
  const isDbAgent = Boolean(
    agent &&
    (
      (agent.name || '').toLowerCase().includes('database') ||
      (agent.name || '').toLowerCase().includes('sql') ||
      (agent.category || '').toLowerCase().includes('database')
    )
  );

  // ── ASDP Agent state ──────────────────────────────────────────────────────
  const isAsdp = agentId === 'asdp-pipeline';
  const [asdpRejectOpen, setAsdpRejectOpen] = useState(false);
  const [asdpRejectFeedback, setAsdpRejectFeedback] = useState('');
  const [asdpRerunOpen, setAsdpRerunOpen] = useState(false);
  const [asdpRerunFeedback, setAsdpRerunFeedback] = useState('');
  const [asdpArtifactsOpen, setAsdpArtifactsOpen] = useState(false);
  const [asdpArtifactsStage, setAsdpArtifactsStage] = useState('');
  const [asdpSuggestionModal, setAsdpSuggestionModal] = useState(null); // { typed, suggested }

  // Derive which connector keys are allowed for this agent.
  // If agent.allowed_connectors is absent/null (custom agents), fall back to showing all.
  // If it is explicitly [] (built-in agents with no connectors), return [] so the button is hidden.
  const _connectorKeyMap = { google_drive: 'drive', google_calendar: 'calendar', github: 'github' };
  const allowedConnectorKeys = (agent?.allowed_connectors == null)
    ? ['drive', 'calendar', 'github']
    : agent.allowed_connectors.map(k => _connectorKeyMap[k]).filter(Boolean);
  const [dbStatus, setDbStatus] = useState({ uploaded: false, db_name: null, tables: [] });
  const [dbUploading, setDbUploading] = useState(false);
  const dbFileInputRef = useRef(null);

  const handleDbFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDbUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/db-agent/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setDbStatus({ uploaded: true, db_name: res.data.db_name, tables: res.data.tables || [] });
      toast.success(`Database "${res.data.db_name}" ready — ${res.data.tables?.length || 0} table(s) found`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload database');
    } finally {
      setDbUploading(false);
      if (dbFileInputRef.current) dbFileInputRef.current.value = '';
    }
  };

  const handleDbClear = () => {
    setDbStatus({ uploaded: false, db_name: null, tables: [] });
    toast.success('Database cleared');
  };

  const messagesEndRef = useRef(null);
  const chatInputRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated || !token) return;
    axios
      .get(`${API}/kb/categories`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setKbCategories(Array.isArray(res.data) ? res.data : []))
      .catch(() => { });
  }, [isAuthenticated, token]);

  useEffect(() => {
    const handler = (e) => {
      if (kbDropdownRef.current && !kbDropdownRef.current.contains(e.target)) {
        setShowKbDropdown(false);
      }
      if (attachMenuRef.current && !attachMenuRef.current.contains(e.target)) {
        setShowAttachMenu(false);
      }
      if (connectorsMenuRef.current && !connectorsMenuRef.current.contains(e.target)) {
        setShowConnectorsMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!isRecording) return;
    const interval = setInterval(() => {
      setRecordingTime((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [isRecording]);

  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (mediaRecorderRef.current && isRecording) mediaRecorderRef.current.stop();
    };
  }, [isRecording]);

  useEffect(() => {
    fetchAgent();
    fetchReviews();
    fetchReviewStats();
    if (isAuthenticated) {
      fetchChatHistory();
      checkFavorite();
    }
  }, [agentId, isAuthenticated]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const autoResizeChatInput = useCallback(() => {
    const textarea = chatInputRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const nextHeight = Math.min(textarea.scrollHeight, CHAT_INPUT_MAX_HEIGHT);
    textarea.style.height = `${Math.max(nextHeight, 48)}px`;
    textarea.style.overflowY = textarea.scrollHeight > CHAT_INPUT_MAX_HEIGHT ? 'auto' : 'hidden';
  }, []);

  useEffect(() => {
    autoResizeChatInput();
  }, [inputMessage, isChatFullscreen, docViewerOpen, autoResizeChatInput]);

  const fetchAgent = async () => {
    try {
      const response = await axios.get(`${API}/agents/${agentId}`);
      setAgent(response.data);
    } catch (error) {
      try {
        const customResponse = await axios.get(`${API}/custom-agents/${agentId}`);
        setAgent(customResponse.data);
      } catch (customError) {
        console.error('Failed to fetch agent:', customError);
        toast.error('Agent not found');
        navigate('/marketplace');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchChatHistory = async () => {
    try {
      const response = await axios.get(`${API}/chat/history/${agentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const historyMessages = [];
      const history = Array.isArray(response.data) ? response.data : [];
      history.reverse().forEach(item => {
        const uploadedDocs = Array.isArray(item.user_uploaded_docs) ? item.user_uploaded_docs : [];
        historyMessages.push({
          role: 'user',
          content: _stripAttachmentMarkers(item.user_message),
          user_uploaded_docs: uploadedDocs,
          attachments: uploadedDocs,
        });
        historyMessages.push({ role: 'assistant', content: item.assistant_response });
      });
      setMessages(historyMessages);
    } catch (error) {
      console.error('Failed to fetch chat history:', error);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/${agentId}`);
      const reviewsData = Array.isArray(response.data) ? response.data : [];
      setReviews(reviewsData);
      if (isAuthenticated && user) {
        setUserHasReviewed(reviewsData.some(r => r.user_id === user.id));
      }
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    }
  };

  const fetchReviewStats = async () => {
    try {
      const response = await axios.get(`${API}/reviews/${agentId}/stats`);
      setReviewStats(response.data);
    } catch (error) {
      console.error('Failed to fetch review stats:', error);
    }
  };

  const checkFavorite = async () => {
    try {
      const response = await axios.get(`${API}/favorites/check/${agentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsFavorite(response.data.is_favorite);
    } catch (error) {
      console.error('Failed to check favorite:', error);
    }
  };

  const toggleFavorite = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to save favorites');
      return;
    }
    setTogglingFavorite(true);
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${agentId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorite(false);
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/favorites`, { agent_id: agentId }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorite(true);
        toast.success('Added to favorites');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update favorites');
    } finally {
      setTogglingFavorite(false);
    }
  };

  const toggleVisibility = async () => {
    if (!agent?.is_custom) return;
    setTogglingVisibility(true);
    try {
      const res = await axios.patch(`${API}/custom-agents/${agentId}/visibility`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgent(prev => ({ ...prev, is_public: res.data.is_public }));
      toast.success(res.data.is_public ? 'Agent is now public' : 'Agent is now private');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update visibility');
    } finally {
      setTogglingVisibility(false);
    }
  };

  const deleteThisAgent = async () => {
    if (!agent?.is_custom) return;
    setDeletingAgent(true);
    try {
      await axios.delete(`${API}/custom-agents/${agentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Agent deleted successfully');
      navigate('/marketplace');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete agent');
      setDeletingAgent(false);
      setConfirmDeleteAgent(false);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    if (!isAuthenticated) {
      toast.error('Please sign in to upload files');
      return;
    }
    for (const file of files) {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        toast.error(`File type ${ext} not allowed`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB)`);
        continue;
      }
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      try {
        const response = await axios.post(`${API}/files/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        setUploadedFiles(prev => [...prev, response.data]);
        toast.success(`${file.name} uploaded`);
      } catch (error) {
        toast.error(error.response?.data?.detail || `Failed to upload ${file.name}`);
      } finally {
        setUploading(false);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return <Image className="w-4 h-4" />;
    return <File className="w-4 h-4" />;
  };

  const startRecording = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to use voice input');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        if (audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          await sendVoiceMessage(audioBlob);
        }
      };
      mediaRecorder.start();
      setRecordingTime(0);
      setIsRecording(true);
      toast.success('Recording started... Click stop when done');
    } catch (error) {
      console.error('Microphone access error:', error);
      toast.error('Could not access microphone. Please allow microphone permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const sendVoiceMessage = async (audioBlob) => {
    if (sending || transcribing) return;
    setTranscribing(true);

    if (user.credits < agent.credits_per_query) {
      toast.error('Insufficient credits for voice input');
      setTranscribing(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('agent_id', agentId);
      formData.append('audio_file', audioBlob, 'recording.webm');

      const tempId = Date.now();
      setMessages(prev => [...prev, { role: 'user', content: 'Transcribing...', isVoice: true, id: tempId }]);

      const response = await axios.post(`${API}/chat/with-voice`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });

      const payload = _axiosData(response);

      setMessages(prev => prev.map(msg =>
        msg.id === tempId
          ? { ...msg, content: `${payload.transcribed_text}` }
          : msg
      ));
      setMessages(prev => [...prev, { role: 'assistant', content: payload.response }]);
      if (updateCredits) updateCredits(payload.remaining_credits);

    } catch (error) {
      console.error('Voice chat error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process voice message');
      setMessages(prev => prev.filter(msg => !msg.isVoice || msg.content !== 'Transcribing...'));
    } finally {
      setTranscribing(false);
    }
  };

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const buildDownloadName = (candidate, fallbackStem, extension, askRename = false) => {
    const stripExt = (name, ext) => {
      const lower = String(name || '').toLowerCase();
      return lower.endsWith(ext) ? String(name).slice(0, -ext.length) : String(name || '');
    };
    const sanitizeStem = (value, fallback) => {
      const cleaned = String(value || '')
        .replace(/[\\/:*?"<>|]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      return cleaned || fallback;
    };
    const initialStem = sanitizeStem(stripExt(candidate, extension), fallbackStem);
    let finalStem = initialStem;
    if (askRename) {
      const renamed = window.prompt('Rename file before download:', initialStem);
      if (renamed === null) return null;
      finalStem = sanitizeStem(stripExt(renamed, extension), initialStem);
    }
    return `${finalStem}${extension}`;
  };

  const downloadGeneratedPpt = async (fileId, filename, askRename = false) => {
    try {
      const response = await axios.get(`${API}/files/download/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob(
        [response.data],
        { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' }
      );
      const url = URL.createObjectURL(blob);
      const downloadName = buildDownloadName(
        filename || `presentation_${new Date().toISOString().slice(0, 10)}.pptx`,
        'presentation', '.pptx', askRename
      );
      if (!downloadName) { URL.revokeObjectURL(url); return; }
      const a = document.createElement('a');
      a.href = url; a.download = downloadName;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download presentation');
    }
  };

  const downloadGeneratedDoc = async (fileId, filename, askRename = false) => {
    try {
      const response = await axios.get(`${API}/files/download/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob(
        [response.data],
        { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }
      );
      const url = URL.createObjectURL(blob);
      const downloadName = buildDownloadName(
        filename || `document_${new Date().toISOString().slice(0, 10)}.docx`,
        'document', '.docx', askRename
      );
      if (!downloadName) { URL.revokeObjectURL(url); return; }
      const a = document.createElement('a');
      a.href = url; a.download = downloadName;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download document');
    }
  };

  const downloadGeneratedZip = async (fileId, filename, askRename = false) => {
    try {
      const response = await axios.get(`${API}/files/download/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const downloadName = buildDownloadName(
        filename || `project_${new Date().toISOString().slice(0, 10)}.zip`,
        'project', '.zip', askRename
      );
      if (!downloadName) { URL.revokeObjectURL(url); return; }
      const a = document.createElement('a');
      a.href = url; a.download = downloadName;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download zip');
    }
  };

  const parsePptDownloadToken = useCallback((content) => {
    const text = String(content || '');
    const pptMatch = text.match(/\[PPT_DOWNLOAD:([^:\]]+):([^\]]+)\]/);
    const docMatch = text.match(/\[DOC_DOWNLOAD:([^:\]]+):([^\]]+)\]/);
    const zipMatch = text.match(/\[ZIP_DOWNLOAD:([^:\]]+):([^\]]+)\]/);
    if (pptMatch) {
      return { cleanContent: text.replace(pptMatch[0], '').trim(), fileId: pptMatch[1], filename: pptMatch[2], fileType: 'ppt' };
    }
    if (docMatch) {
      return { cleanContent: text.replace(docMatch[0], '').trim(), fileId: docMatch[1], filename: docMatch[2], fileType: 'doc' };
    }
    if (zipMatch) {
      return { cleanContent: text.replace(zipMatch[0], '').trim(), fileId: zipMatch[1], filename: zipMatch[2], fileType: 'zip' };
    }
    return { cleanContent: text, fileId: null, filename: null, fileType: null };
  }, []);

  const handleDocSave = useCallback(async (updatedDocData) => {
    if (!docViewerFileId) return;
    setDocSaving(true);
    try {
      await axios.post(`${API}/doc/update`, {
        file_id: docViewerFileId,
        doc_data: updatedDocData
      }, { headers: { Authorization: `Bearer ${token}` } });
      setDocViewerDataMap(prev => ({ ...prev, [docViewerFileId]: updatedDocData }));
      toast.success('Document saved and rebuilt');
    } catch (error) {
      console.error('Doc save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save document');
    } finally {
      setDocSaving(false);
    }
  }, [docViewerFileId, token]);

  // ── NEW: handleClearChat ───────────────────────────────────────────────────
  const handleClearChat = async () => {
    try {
      await axios.delete(`${API}/chat/history/${agentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      // If endpoint doesn't exist, silently ignore — still clear local state
      console.warn('Clear chat API call failed (may not exist):', err);
    }
    setMessages([]);
    toast.success('Chat cleared');
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  sendMessage
  // ─────────────────────────────────────────────────────────────────────────
  const sendMessage = async () => {
    if ((!inputMessage.trim() && uploadedFiles.length === 0) || sending) return;

    if (!isAuthenticated) {
      toast.error('Please sign in to chat with agents');
      navigate('/login');
      return;
    }

    // ── ASDP: detect command typos and surface a suggestion ───────────────
    if (isAsdp && inputMessage.trim() && uploadedFiles.length === 0) {
      const suggestion = detectAsdpCommandSuggestion(inputMessage.trim());
      if (suggestion) {
        setAsdpSuggestionModal(suggestion);
        return; // halt send until user decides
      }
    }

    const userTier = (user?.plan || 'free').toLowerCase();
    const agentTier = (agent?.tier || 'free').toLowerCase();
    if (TIER_HIERARCHY[userTier] < TIER_HIERARCHY[agentTier]) {
      // User is too low tier, and it's not their own custom agent
      if (!(agent?.is_custom && agent?.creator_id === user?.id)) {
        toast.error(`Please upgrade to a ${agentTier.toUpperCase()} plan to use this agent.`);
        return;
      }
    }

    const baseCredits = agent.credits_per_query;
    const fileMultiplier = 1 + (uploadedFiles.length * 0.5);
    const estimatedCredits = Math.ceil(baseCredits * fileMultiplier);
    if (user.credits < estimatedCredits) {
      toast.error('Insufficient credits for this request.');
      return;
    }

    // Build tool hints
    const toolHints = [
      webSearchEnabled && '[WEB_SEARCH]',
      deepResearchEnabled && '[DEEP_RESEARCH]',
      imageGenEnabled && '[IMAGE_GEN]',
    ].filter(Boolean).join(' ');

    const currentMessage = inputMessage + (toolHints ? ` ${toolHints}` : '');
    const currentFiles = [...uploadedFiles];
    const currentDriveFiles = [...selectedDriveFiles];
    const outgoingAttachments = [
      ...currentFiles.map(f => ({
        id: f.id,
        source: 'local_upload',
        original_filename: f.original_filename,
        file_type: f.file_type,
      })),
      ...currentDriveFiles.map(f => ({
        id: f.id,
        source: 'google_drive',
        original_filename: f.name,
        file_type: f.mimeType,
      })),
    ];
    const userMessage = {
      role: 'user',
      content: currentMessage.trim() || (outgoingAttachments.length > 0 ? 'Attached files' : currentMessage),
      attachments: outgoingAttachments,
      user_uploaded_docs: outgoingAttachments,
    };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setUploadedFiles([]);
    setSelectedDriveFiles([]);
    setSending(true);
    setStreamEvents([]);
    const collectedStreamEvents = [];

    try {
      const STREAMING_AGENTS = ['tech-code-review'];
      const useTokenStreaming = STREAMING_AGENTS.includes(agentId) && currentFiles.length === 0;
      const useDocStreaming = (agentId === 'doc-creation-agent' || agentId === 'requirements-doc-agent') && currentFiles.length === 0;

      // ── Path 1: token-by-token SSE ──────────────────────────────────────
      if (useTokenStreaming) {
        const streamId = `stream-${Date.now()}`;
        setMessages(prev => [...prev, { role: 'assistant', content: '', _streamId: streamId }]);
        const controller = new AbortController();
        const hardTimeoutMs = 120000;
        const idleTimeoutMs = 30000;
        let hardTimeoutId = null;
        let idleTimeoutId = null;

        const resetIdleTimeout = () => {
          if (idleTimeoutId) clearTimeout(idleTimeoutId);
          idleTimeoutId = setTimeout(() => {
            controller.abort('idle-timeout');
          }, idleTimeoutMs);
        };

        hardTimeoutId = setTimeout(() => {
          controller.abort('hard-timeout');
        }, hardTimeoutMs);
        resetIdleTimeout();

        const activeConnectors1 = Object.entries(connectedApps).filter(([, v]) => v).map(([k]) => ({ drive: 'google_drive', calendar: 'google_calendar', github: 'github' }[k] || k));
        const driveIds1 = currentDriveFiles.map(f => f.id);
        const sseResponse = await fetch(`${API}/chat/stream`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          signal: controller.signal,
          body: JSON.stringify({
            agent_id: agentId,
            message: currentMessage,
            ...(kbConnected && kbCategory ? { kb_category: kbCategory } : {}),
            ...(activeConnectors1.length > 0 && { connectors: activeConnectors1 }),
            ...(driveIds1.length > 0 && { gdrive_file_ids: driveIds1 }),
          }),
        });
        if (!sseResponse.ok || !sseResponse.body) {
          const errBody = await sseResponse.json().catch(() => ({}));
          throw new Error(errBody.detail || `Stream failed (${sseResponse.status})`);
        }

        const reader = sseResponse.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let stopStreaming = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done || stopStreaming) break;
          resetIdleTimeout();
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop();

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const data = JSON.parse(line.slice(6));
              if (data.token) {
                setMessages(prev =>
                  prev.map(msg =>
                    msg._streamId === streamId
                      ? { ...msg, content: msg.content + data.token }
                      : msg
                  )
                );
              } else if (data.type === 'final' && data.response) {
                // Complete response from _event_gen (connector-tools path)
                setMessages(prev =>
                  prev.map(msg =>
                    msg._streamId === streamId
                      ? { role: 'assistant', content: data.response }
                      : msg
                  )
                );
                updateCredits(data.remaining_credits);
                await reader.cancel();
                stopStreaming = true;
                break;
              } else if (data.done) {
                updateCredits(data.remaining_credits);
                setMessages(prev =>
                  prev.map(msg =>
                    msg._streamId === streamId
                      ? { role: 'assistant', content: msg.content }
                      : msg
                  )
                );
              } else if (data.type === 'error' || data.error) {
                toast.error(data.message || data.error);
                await reader.cancel();
                stopStreaming = true;
                break;
              }
            } catch {
              // ignore malformed lines
            }
          }
        }

        if (hardTimeoutId) clearTimeout(hardTimeoutId);
        if (idleTimeoutId) clearTimeout(idleTimeoutId);

        // ── Path 2: doc-creation SSE with progress events ───────────────────
      } else if (useDocStreaming) {
        const activeConnectors2 = Object.entries(connectedApps).filter(([, v]) => v).map(([k]) => ({ drive: 'google_drive', calendar: 'google_calendar', github: 'github' }[k] || k));
        const driveIds2 = currentDriveFiles.map(f => f.id);
        const streamResponse = await fetch(`${API}/chat/stream`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            agent_id: agentId,
            message: currentMessage,
            ...(kbConnected && kbCategory ? { kb_category: kbCategory } : {}),
            ...(activeConnectors2.length > 0 && { connectors: activeConnectors2 }),
            ...(driveIds2.length > 0 && { gdrive_file_ids: driveIds2 }),
          }),
        });
        if (!streamResponse.ok || !streamResponse.body) throw new Error('Failed to start streaming response');

        const reader = streamResponse.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let finalPayload = null;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const chunks = buffer.split('\n\n');
          buffer = chunks.pop() || '';

          for (const chunk of chunks) {
            const dataLine = chunk.split('\n').find(l => l.startsWith('data:'));
            if (!dataLine) continue;
            let payload;
            try { payload = JSON.parse(dataLine.slice(5).trim()); } catch { continue; }

            if (payload.type === 'status' || payload.type === 'agent_step') {
              collectedStreamEvents.push(payload);
              setStreamEvents(prev => [...prev, payload]);
              continue;
            }
            if (payload.type === 'final') { finalPayload = payload; continue; }
            if (payload.type === 'error') throw new Error(payload.message || 'Streaming failed');
          }
        }

        if (!finalPayload) throw new Error('No final response received');

        const syntheticResponse = { data: { ...finalPayload, stream_events: collectedStreamEvents } };
        const ssePayload = _sseData(syntheticResponse);

        setMessages(prev => [...prev, {
          role: 'assistant',
          content: ssePayload.response,
          streamEvents: Array.isArray(ssePayload.stream_events) ? ssePayload.stream_events : [],
        }]);
        updateCredits(ssePayload.remaining_credits);

        if (ssePayload.doc_data) {
          const parsed = parsePptDownloadToken(ssePayload.response);
          setDocViewerDataMap(prev => ({ ...prev, [parsed.fileId]: ssePayload.doc_data }));
          setDocViewerFileId(parsed.fileId);
          setDocViewerFilename(parsed.filename);
          setDocViewerOpen(true);
        }

        // ── Path 3: standard axios POST /chat ───────────────────────────────
      } else {
        const activeConnectors = Object.entries(connectedApps).filter(([, v]) => v).map(([k]) => {
          return { drive: 'google_drive', calendar: 'google_calendar', github: 'github' }[k] || k;
        });
        const driveFileIds = currentDriveFiles.map(f => f.id);

        const payload = {
          agent_id: agentId,
          message: currentMessage || (currentFiles.length > 0 ? 'Please analyze the attached file(s).' : ''),
          ...(currentFiles.length > 0 && { file_ids: currentFiles.map(f => f.id).join(',') }),
          ...(kbConnected && kbCategory && { kb_category: kbCategory }),
          ...(activeConnectors.length > 0 && { connectors: activeConnectors }),
          ...(driveFileIds.length > 0 && { gdrive_file_ids: driveFileIds }),
        };

        const response = await axios.post(`${API}/chat`, payload, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });

        const chatData = _axiosData(response);

        setMessages(prev => [...prev, {
          role: 'assistant',
          content: chatData.response,
          streamEvents: [],
        }]);
        updateCredits(chatData.remaining_credits);

        if (chatData.doc_data) {
          const parsed = parsePptDownloadToken(chatData.response);
          setDocViewerDataMap(prev => ({ ...prev, [parsed.fileId]: chatData.doc_data }));
          setDocViewerFileId(parsed.fileId);
          setDocViewerFilename(parsed.filename);
          setDocViewerOpen(true);
        }
      }

    } catch (error) {
      console.error('Chat error:', error);
      if (error?.name === 'AbortError') {
        toast.error('Tech code review timed out. Please retry with a shorter query.');
      } else {
        toast.error(error.response?.data?.detail || error.message || 'Failed to send message');
      }
      setMessages(prev => {
        const cleaned = prev.filter(msg => !msg._streamId);
        return cleaned.slice(0, -1);
      });
    } finally {
      setSending(false);
      setStreamEvents([]);
    }
  };

  const handleInputKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }, [sendMessage]);

  // ── ASDP helpers ──────────────────────────────────────────────────────────
  const ASDP_COMMANDS = ['launch', 'approve', 'reject', 'rerun', 'status', 'artifacts', 'events', 'reset', 'export', 'download', 'help'];
  const ASDP_STAGES = [
    { id: 'requirements', label: 'Requirements Analysis' },
    { id: 'system_design', label: 'System Design' },
    { id: 'task_planning', label: 'Task Planning' },
    { id: 'research', label: 'Research' },
    { id: 'code_generation', label: 'Code Generation' },
    { id: 'code_review', label: 'Code Review' },
    { id: 'testing', label: 'Testing' },
    { id: 'devops', label: 'DevOps' },
  ];

  const levenshtein = (a, b) => {
    const m = a.length, n = b.length;
    const dp = Array.from({ length: m + 1 }, (_, i) => Array.from({ length: n + 1 }, (_, j) => j === 0 ? i : 0));
    for (let j = 1; j <= n; j++) dp[0][j] = j;
    for (let i = 1; i <= m; i++) for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
    return dp[m][n];
  };

  const detectAsdpCommandSuggestion = (text) => {
    const lower = text.trim().toLowerCase();
    // Already a valid command
    if (ASDP_COMMANDS.some(cmd => lower === cmd || lower.startsWith(cmd + ' ') || lower === '/' + cmd)) return null;
    if (['yes', 'lgtm', 'looks good', 'approved'].includes(lower)) return null;
    const firstWord = lower.split(' ')[0].replace(/^\//, '');
    if (firstWord.length < 3) return null;
    let best = null, bestDist = Infinity;
    for (const cmd of ASDP_COMMANDS) {
      const d = levenshtein(firstWord, cmd);
      if (d < bestDist && d <= 2 && d > 0) { bestDist = d; best = cmd; }
    }
    return (best && firstWord !== best) ? { typed: firstWord, suggested: best } : null;
  };

  const sendAsdpCommand = useCallback((cmdText) => {
    setInputMessage(cmdText);
    setTimeout(() => {
      setInputMessage('');
      const userMessage = { role: 'user', content: cmdText };
      setMessages(prev => [...prev, userMessage]);
      setSending(true);
      setStreamEvents([]);
      const runSend = async () => {
        try {
          const response = await axios.post(`${API}/chat`, {
            agent_id: agentId,
            message: cmdText,
          }, { headers: { Authorization: `Bearer ${token}` } });
          const data = response.data?.data ?? response.data ?? {};
          const reply = data.response || data.message || 'Done.';
          setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
          if (data.credits_used) updateCredits(data.credits_used);
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Command failed');
          setMessages(prev => [...prev, { role: 'assistant', content: '⚠️ Command failed. Please try again.' }]);
        } finally {
          setSending(false);
          setStreamEvents([]);
        }
      };
      runSend();
    }, 0);
  }, [agentId, token, updateCredits]);

  // ── Fullscreen now hides the sidebar for max chat width ───────────────────
  const toggleChatFullscreen = () => setIsChatFullscreen(v => !v);

  const submitReview = async () => {
    if (!reviewForm.title.trim() || !reviewForm.comment.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/reviews`, {
        agent_id: agentId,
        rating: reviewForm.rating,
        title: reviewForm.title,
        comment: reviewForm.comment
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Review submitted successfully!');
      setShowReviewForm(false);
      setReviewForm({ rating: 5, title: '', comment: '' });
      fetchReviews();
      fetchReviewStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const exportChatHistory = async (format) => {
    try {
      const response = await axios.get(`${API}/chat/history/${agentId}/export?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const contentDisposition = response.headers['content-disposition'];
      let filename = `chat_history_${agentId}_${new Date().toISOString().split('T')[0]}.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match?.[1]) filename = match[1];
      }
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success(`Chat history exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export chat history');
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName];
    return IconComponent ? <IconComponent className="w-8 h-8" /> : <Cpu className="w-8 h-8" />;
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'free': return 'bg-green-100 text-green-700';
      case 'pro': return 'bg-blue-100 text-blue-700';
      case 'enterprise': return 'bg-purple-100 text-purple-700';
      case 'enterprise_pro': return 'bg-gradient-to-r from-purple-100 to-orange-100 text-purple-800';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const renderStars = (rating, interactive = false, onChange = null) => (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => interactive && onChange && onChange(star)}
          className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
          disabled={!interactive}
        >
          <Star className={`w-5 h-5 ${star <= rating ? 'fill-[#F87217] text-[#F87217]' : 'text-slate-300'}`} />
        </button>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#F87217]" />
      </div>
    );
  }

  if (!agent) return null;

  return (
    <div className="h-screen overflow-hidden flex flex-col page-enter" style={{ background: isDark ? '#0a0f1e' : '#fff' }}>
      {/* ── Top Navbar ────────────────────────────────────────────────────── */}
      <nav className="flex-shrink-0 z-50 border-b" style={{ background: isDark ? '#0f1729' : '#fff', borderColor: isDark ? '#1e2d52' : '#e2e8f0' }}>
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/marketplace')}
              className="text-slate-600 hover:text-slate-900"
              data-testid="back-to-marketplace"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Marketplace
            </Button>
          </div>
          {isAuthenticated && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-sm" style={{ background: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9' }}>
                <Coins className="w-4 h-4 text-[#F87217]" />
                <span className="text-sm font-medium" style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}>{user?.credits || 0}</span>
              </div>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="rounded-sm"
                data-testid="nav-dashboard"
              >
                Dashboard
              </Button>
            </div>
          )}
        </div>
      </nav>

      {/* ── Mobile backdrop for sidebar drawer ───────────────────────────── */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* ── Main body: sidebar + chat ──────────────────────────────────────── */}
      <div className="flex flex-1 min-h-0 overflow-hidden">

        {/* ── Left Panel: Agent Details ──────────────────────────────────── */}
        {/* Mobile: fixed drawer overlay; Desktop: collapsible sidebar */}
        <div
          className={`
            flex-shrink-0 flex flex-col overflow-x-hidden overflow-y-auto
            transition-all duration-300 ease-in-out border-r
            fixed md:relative z-40 md:z-auto inset-y-0 left-0 top-0
            ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
            ${sidebarCollapsed ? 'md:w-0 md:border-r-0 md:opacity-0' : 'md:w-[320px]'}
            w-[300px]
          `}
          style={{
            background: isDark ? '#0a0f1e' : '#f8fafc',
            borderColor: isDark ? '#1e2d52' : '#e2e8f0',
            maxHeight: '100%',
            top: 0,
          }}
        >
          {/* Sidebar toggle button (desktop only, at top) */}
          <div
            className="hidden md:flex items-center justify-between px-3 py-2 border-b flex-shrink-0"
            style={{ borderColor: isDark ? '#1e2d52' : '#e2e8f0' }}
          >
            {!sidebarCollapsed && (
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: isDark ? '#8b949e' : '#94a3b8' }}>Agent Info</span>
            )}
            <button
              onClick={() => setSidebarCollapsed(v => !v)}
              className={`p-1.5 rounded-md transition-all ml-auto ${
                isDark ? 'text-slate-400 hover:bg-white/10 hover:text-white' : 'text-slate-500 hover:bg-slate-200 hover:text-slate-800'
              }`}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
          </div>

            {/* Agent icon + tier + name */}
            <div className="p-5 border-b" style={{ borderColor: isDark ? '#1e2d52' : '#f1f5f9' }}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 bg-slate-100 rounded-sm flex items-center justify-center text-[#F87217]">
                    {getIcon(agent.icon)}
                  </div>
                  <div>
                    <Badge className={`${getTierColor(agent.tier)} border-0 rounded-sm mb-1`}>
                      {agent.tier}
                    </Badge>
                    <p className="text-xs text-slate-500">{agent.category}</p>
                  </div>
                </div>
                <button
                  onClick={toggleFavorite}
                  disabled={togglingFavorite}
                  className={`p-2 rounded-sm transition-all ${isFavorite
                    ? 'bg-red-50 text-red-500 hover:bg-red-100'
                    : isDark ? 'text-slate-500 hover:text-slate-200 hover:bg-white/10' : 'bg-slate-100 text-slate-400 hover:bg-slate-200 hover:text-slate-600'
                    }`}
                  style={isFavorite ? {} : (isDark ? { background: 'rgba(255,255,255,0.05)' } : {})}
                  data-testid="favorite-btn"
                  title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                >
                  <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
                </button>
              </div>

              <h1 className="text-lg font-bold font-['Outfit'] mb-2" style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}>{formatAgentName(agent.name)}</h1>

              {reviewStats && reviewStats.total_reviews > 0 && (
                <div className="flex items-center gap-2 mb-3">
                  {renderStars(Math.round(reviewStats.average_rating))}
                  <span className={`text-xs font-medium ${isDark ? 'text-slate-200' : 'text-slate-900'}`}>{reviewStats.average_rating}</span>
                  <span className="text-xs text-slate-500">({reviewStats.total_reviews})</span>
                </div>
              )}

              <p className="text-sm leading-relaxed" style={{ color: isDark ? '#8b949e' : '#475569' }}>{agent.description}</p>
            </div>

            {/* Credits per query */}
            <div className="px-5 py-3 border-b" style={{ borderColor: isDark ? '#1e2d52' : '#f1f5f9' }}>
              <div className="flex items-center gap-2 p-2.5 bg-[#F87217]/5 border border-[#F87217]/20 rounded-sm">
                <Coins className="w-4 h-4 text-[#F87217]" />
                <span className="text-sm font-medium" style={{ color: isDark ? '#e6edf3' : '#0d1a3a' }}>{agent.credits_per_query} credits</span>
                <span className="text-xs text-slate-500">per query</span>
              </div>
            </div>

            {/* Edit agent (custom agents, creator only) */}
            {agent.is_custom && isAuthenticated && user && agent.creator_id === user.id && (
              <div className="px-5 py-3 border-b" style={{ borderColor: isDark ? '#1e2d52' : '#f1f5f9' }}>
                <button
                  onClick={() => navigate(`/builder/${agentId}`)}
                  className={`flex items-center gap-2 w-full p-2.5 rounded-sm border text-sm font-medium transition-all ${
                    isDark
                      ? 'border-[#F87217]/40 bg-[#F87217]/10 text-[#F87217] hover:bg-[#F87217]/20'
                      : 'border-[#F87217]/30 bg-[#F87217]/5 text-[#F87217] hover:bg-[#F87217]/10'
                  }`}
                >
                  <Pencil className="w-4 h-4" />
                  Edit Agent
                </button>
              </div>
            )}

            {/* Visibility toggle (custom agents only) */}
            {agent.is_custom && isAuthenticated && user && agent.creator_id === user.id && (
              <div className="px-5 py-3 border-b" style={{ borderColor: isDark ? '#1e2d52' : '#f1f5f9' }}>
                <button
                  onClick={toggleVisibility}
                  disabled={togglingVisibility}
                  className={`flex items-center gap-2 w-full p-2.5 rounded-sm border text-sm font-medium transition-all ${agent.is_public
                    ? 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
                    : isDark ? 'border-[#1e2d52] text-[#c9d1d9] hover:border-[#F87217] hover:text-[#F87217]' : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100'
                    }`}
                  style={!agent.is_public && isDark ? { background: 'rgba(255,255,255,0.04)' } : {}}
                >
                  {agent.is_public ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  {agent.is_public ? 'Public — visible to everyone' : 'Private — only you'}
                </button>
              </div>
            )}

            {/* Delete agent (custom agents, creator only) */}
            {agent.is_custom && isAuthenticated && user && agent.creator_id === user.id && (
              <div className="px-5 py-3 border-b" style={{ borderColor: isDark ? '#1e2d52' : '#f1f5f9' }}>
                {confirmDeleteAgent ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-red-500 font-medium flex-1">Delete this agent?</span>
                    <button
                      onClick={deleteThisAgent}
                      disabled={deletingAgent}
                      className="text-xs px-2.5 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-sm font-medium transition-colors disabled:opacity-50"
                    >
                      {deletingAgent ? 'Deleting…' : 'Yes, delete'}
                    </button>
                    <button
                      onClick={() => setConfirmDeleteAgent(false)}
                      disabled={deletingAgent}
                      className="text-xs px-2.5 py-1.5 border rounded-sm font-medium transition-colors"
                      style={{ borderColor: isDark ? '#2d3f6e' : '#e2e8f0', color: isDark ? '#c9d1d9' : '#475569' }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmDeleteAgent(true)}
                    className="flex items-center gap-2 w-full p-2.5 rounded-sm border text-sm font-medium transition-all border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Agent
                  </button>
                )}
              </div>
            )}

            {/* Features / Use Cases / Goals */}
            <div className="px-5 py-4 border-b border-slate-100 space-y-4">
              {agent.is_custom ? (
                <>
                  {agent.goals && (
                    <div>
                      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Goals</h3>
                      <div className="space-y-1.5">
                        {agent.goals.split('\n').filter(l => l.trim()).map((goal, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm text-slate-600">
                            <Zap className="w-3.5 h-3.5 text-[#F87217] mt-0.5 flex-shrink-0" />
                            {goal.replace(/^[-•*]\s*/, '')}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {(agent.features || []).length > 0 && (
                    <div>
                      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Features</h3>
                      <div className="space-y-1.5">
                        {agent.features.map((feature, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm text-slate-600">
                            <Check className="w-3.5 h-3.5 text-[#F87217] mt-0.5 flex-shrink-0" />{feature}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {(agent.use_cases || []).length > 0 && (
                    <div>
                      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Use Cases</h3>
                      <div className="space-y-1.5">
                        {agent.use_cases.map((useCase, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm text-slate-600">
                            <Zap className="w-3.5 h-3.5 text-[#F87217] mt-0.5 flex-shrink-0" />{useCase}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ── Reviews section (collapsible) ──────────────────────────── */}
            <div className="border-b" style={{ borderColor: isDark ? '#1e2d52' : '#f1f5f9' }}>
              <button
                onClick={() => setSidebarReviewsOpen(v => !v)}
                className="w-full flex items-center justify-between px-5 py-3 text-sm font-semibold transition-colors"
                style={{ color: isDark ? '#c9d1d9' : '#374151' }}
                onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                data-testid="sidebar-reviews-toggle"
              >
                <span className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-[#F87217]" />
                  Reviews ({reviewStats?.total_reviews || 0})
                </span>
                {sidebarReviewsOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </button>

              {sidebarReviewsOpen && (
                <div className="px-5 pb-4">
                  {/* Rating summary */}
                  {reviewStats && reviewStats.total_reviews > 0 && (
                    <div className="mb-4 p-3 rounded-sm" style={{ background: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9'}` }}>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl font-bold" style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}>{reviewStats.average_rating}</span>
                        <div>
                          {renderStars(Math.round(reviewStats.average_rating))}
                          <p className="text-xs text-slate-500 mt-0.5">{reviewStats.total_reviews} reviews</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        {[5, 4, 3, 2, 1].map((star) => {
                          const count = reviewStats.rating_distribution?.[star] || 0;
                          const pct = reviewStats.total_reviews > 0 ? (count / reviewStats.total_reviews) * 100 : 0;
                          return (
                            <div key={star} className="flex items-center gap-2">
                              <span className="text-xs text-slate-500 w-5">{star}★</span>
                              <Progress value={pct} className="flex-1 h-1.5" />
                              <span className="text-xs text-slate-400 w-4">{count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Write review button */}
                  {isAuthenticated && !userHasReviewed && !showReviewForm && (
                    <Button
                      onClick={() => setShowReviewForm(true)}
                      className="w-full mb-3 bg-[#FF4F00] hover:bg-[#CC3F00] text-white rounded-sm text-xs"
                      data-testid="write-review-btn"
                    >
                      <Star className="w-3.5 h-3.5 mr-1.5" />Write a Review
                    </Button>
                  )}

                  {/* Review form */}
                  {showReviewForm && (
                    <div className={`mb-4 p-3 border rounded-sm ${isDark ? 'bg-[#0f1729] border-[#1e2d52]' : 'border-slate-200 bg-slate-50'}`}>
                      <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-slate-200' : 'text-slate-900'}`}>Write Your Review</h3>
                      <div className="space-y-3">
                        <div>
                          <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Rating</label>
                          {renderStars(reviewForm.rating, true, (rating) => setReviewForm(prev => ({ ...prev, rating })))}
                        </div>
                        <div>
                          <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Title</label>
                          <Input
                            value={reviewForm.title}
                            onChange={(e) => setReviewForm(prev => ({ ...prev, title: e.target.value }))}
                            placeholder="Summarize your experience"
                            className={`rounded-sm text-xs h-8 ${isDark ? 'bg-[#0a0f1e] border-[#1e2d52] text-slate-200 placeholder:text-slate-500' : ''}`}
                            data-testid="review-title-input"
                          />
                        </div>
                        <div>
                          <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Review</label>
                          <Textarea
                            value={reviewForm.comment}
                            onChange={(e) => setReviewForm(prev => ({ ...prev, comment: e.target.value }))}
                            placeholder="Share your experience..."
                            rows={3}
                            className={`rounded-sm text-xs ${isDark ? 'bg-[#0a0f1e] border-[#1e2d52] text-slate-200 placeholder:text-slate-500' : ''}`}
                            data-testid="review-comment-input"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={submitReview}
                            disabled={submittingReview}
                            className="flex-1 bg-[#F87217] hover:bg-[#C65B12] text-white rounded-sm text-xs h-8"
                            data-testid="submit-review-btn"
                          >
                            {submittingReview ? 'Submitting...' : 'Submit'}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => setShowReviewForm(false)}
                            className="rounded-sm text-xs h-8"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Review list */}
                  <div className="space-y-3">
                    {reviews.length === 0 ? (
                      <div className="text-center py-6">
                        <Star className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                        <p className="text-xs text-slate-400">No reviews yet</p>
                      </div>
                    ) : (
                      reviews.map((review) => (
                        <div key={review.id} className="p-3 rounded-sm" style={{ border: `1px solid ${isDark ? '#1e2d52' : '#e2e8f0'}` }} data-testid={`review-${review.id}`}>
                          <div className="flex items-start justify-between mb-1">
                            <p className="text-xs font-semibold" style={{ color: isDark ? '#e6edf3' : '#0d1a3a' }}>{review.user_name}</p>
                            <span className="text-[10px] text-slate-400">{new Date(review.created_at).toLocaleDateString()}</span>
                          </div>
                          {renderStars(review.rating)}
                          <h4 className="text-xs font-medium mt-1.5 mb-0.5" style={{ color: isDark ? '#e6edf3' : '#0d1a3a' }}>{review.title}</h4>
                          <p className="text-xs text-slate-500 leading-relaxed">{review.comment}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Sign up CTA (unauthenticated) */}
            {!isAuthenticated && (
              <div className="px-5 py-4">
                <Button
                  className="w-full bg-[#FF4F00] hover:bg-[#CC3F00] text-white rounded-sm btn-primary-glow"
                  onClick={() => navigate('/register')}
                  data-testid="signup-to-chat"
                >
                  Sign Up to Start Chatting
                </Button>
              </div>
            )}

            {/* Spacer to push content flush */}
            <div className="flex-1" />
          </div>

        {/* ── Right Panel: Chat Area (flex-1, full height) ──────────────────── */}
        <div className="flex-1 flex min-w-0 overflow-hidden">
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden" style={{ background: isDark ? '#0a0f1e' : '#fff' }}>
            {/* Chat Header */}
            <div
              className={`flex-shrink-0 px-4 py-3 flex items-center justify-between border-b ${isDark ? 'border-[#1e2d52]' : 'border-slate-200'}`}
              style={{ background: isDark ? '#0f1729' : 'linear-gradient(135deg, #fff 0%, #fff8f5 100%)' }}
            >
              <div className="flex items-center gap-2">
                {/* Mobile hamburger to open sidebar */}
                <button
                  className="md:hidden p-1.5 rounded-md mr-1 transition-colors"
                  style={{ color: isDark ? '#8b949e' : '#64748b' }}
                  onClick={() => setMobileSidebarOpen(v => !v)}
                  title="Toggle agent info"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                {/* Desktop sidebar toggle when collapsed */}
                {sidebarCollapsed && (
                  <button
                    className="hidden md:flex p-1.5 rounded-md mr-1 transition-colors hover:bg-slate-200"
                    style={{ color: isDark ? '#8b949e' : '#64748b' }}
                    onClick={() => setSidebarCollapsed(false)}
                    title="Expand agent info"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                )}
                {/* Agent icon with glow ring */}
                <div className="relative">
                  <div
                    className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF4F00] to-[#F87217] flex items-center justify-center shadow-md"
                    style={{ boxShadow: '0 0 0 3px rgba(248,114,23,0.15)' }}
                  >
                    <MessageSquare className="w-4 h-4 text-white" />
                  </div>
                  {/* Online pulse dot */}
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-white shadow-sm">
                    <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />
                  </span>
                </div>
                <div>
                  <h2 className="font-semibold text-sm leading-tight" style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}>Chat with {formatAgentName(agent.name)}</h2>
                  <p className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
                    AI-powered assistant · Online
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1.5">
                {/* Desktop Export buttons */}
                <div className="hidden md:flex items-center gap-1.5">
                  {isAuthenticated && messages.length > 0 && (
                    <>
                      <button
                        onClick={() => exportChatHistory('json')}
                        className="text-xs rounded-full h-7 px-3 flex items-center border transition-colors"
                        style={{ background: 'transparent', borderColor: isDark ? '#1e2d52' : '#e2e8f0', color: isDark ? '#c9d1d9' : '#374151' }}
                        onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : '#f8fafc'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                        data-testid="export-json-btn"
                      >
                        <Download className="w-3 h-3 mr-1" />JSON
                      </button>
                      <button
                        onClick={() => exportChatHistory('csv')}
                        className="text-xs rounded-full h-7 px-3 flex items-center border transition-colors"
                        style={{ background: 'transparent', borderColor: isDark ? '#1e2d52' : '#e2e8f0', color: isDark ? '#c9d1d9' : '#374151' }}
                        onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : '#f8fafc'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                        data-testid="export-csv-btn"
                      >
                        <Download className="w-3 h-3 mr-1" />CSV
                      </button>
                    </>
                  )}

                  {/* ── Clear Chat button ─────────────────────────────── */}
                  {isAuthenticated && (
                    <button
                      onClick={handleClearChat}
                      disabled={messages.length === 0 || sending}
                      className="text-xs rounded-full h-7 px-3 flex items-center border transition-colors disabled:opacity-40"
                      style={{ borderColor: isDark ? '#7f1d1d' : '#fecaca', color: isDark ? '#f87171' : '#ef4444' }}
                      onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = isDark ? 'rgba(239,68,68,0.12)' : '#fff1f2'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      title="Clear chat history"
                      data-testid="clear-chat-btn"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />Clear
                    </button>
                  )}
                </div>

                {/* Mobile Three-Dots Menu */}
                <div className="md:hidden relative">
                  {(isAuthenticated && (messages.length > 0 || true)) && (
                    <button
                      onClick={() => setShowMobileHeaderMenu(!showMobileHeaderMenu)}
                      className="p-1.5 rounded-md transition-colors"
                      style={{ color: isDark ? '#8b949e' : '#64748b' }}
                      title="Options"
                    >
                      <MoreVertical className="w-5 h-5" />
                    </button>
                  )}
                  {showMobileHeaderMenu && (
                    <div className="absolute right-0 top-full mt-2 w-48 rounded-md shadow-lg border z-50 overflow-hidden" style={{ background: isDark ? '#0f1729' : '#fff', borderColor: isDark ? '#1e2d52' : '#e2e8f0' }}>
                      <div className="py-1">
                        {messages.length > 0 && (
                          <>
                            <button
                              onClick={() => { exportChatHistory('json'); setShowMobileHeaderMenu(false); }}
                              className={`w-full text-left px-4 py-2.5 text-xs font-medium flex items-center transition-colors ${isDark ? 'text-slate-200 hover:bg-white/5' : 'text-slate-700 hover:bg-slate-50'}`}
                            >
                              <Download className="w-3.5 h-3.5 mr-2" /> Download JSON
                            </button>
                            <button
                              onClick={() => { exportChatHistory('csv'); setShowMobileHeaderMenu(false); }}
                              className={`w-full text-left px-4 py-2.5 text-xs font-medium flex items-center transition-colors ${isDark ? 'text-slate-200 hover:bg-white/5' : 'text-slate-700 hover:bg-slate-50'}`}
                            >
                              <Download className="w-3.5 h-3.5 mr-2" /> Download CSV
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => { handleClearChat(); setShowMobileHeaderMenu(false); }}
                          disabled={messages.length === 0 || sending}
                          className={`w-full text-left px-4 py-2.5 text-xs font-medium flex items-center transition-colors ${isDark ? 'text-red-400 hover:bg-red-500/10' : 'text-red-600 hover:bg-red-50'} disabled:opacity-50`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-2" /> Clear Chat
                        </button>
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </div>

            {/* Database Agent Upload Panel */}
            {isDbAgent && isAuthenticated && (
              <div className="flex-shrink-0 px-4 pt-3 pb-1">
                {dbStatus.uploaded ? (
                  <div className="flex items-center justify-between gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-sm">
                    <div className="flex items-center gap-2">
                      <Database className="w-5 h-5 text-emerald-600" />
                      <div>
                        <span className="font-medium text-emerald-800 text-sm">{dbStatus.db_name}</span>
                        <span className="text-emerald-600 text-xs ml-2">
                          ({dbStatus.tables?.length || 0} table{dbStatus.tables?.length !== 1 ? 's' : ''})
                        </span>
                        {dbStatus.tables && dbStatus.tables.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {dbStatus.tables.map(t => (
                              <span key={t} className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[10px] font-mono">{t}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <input type="file" ref={dbFileInputRef} onChange={handleDbFileUpload} accept=".db,.sqlite,.sqlite3" className="hidden" />
                      <Button
                        variant="outline" size="sm"
                        onClick={() => dbFileInputRef.current?.click()}
                        disabled={dbUploading}
                        className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                      >
                        {dbUploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}Replace
                      </Button>
                      <Button
                        variant="outline" size="sm"
                        onClick={handleDbClear}
                        className="text-xs border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />Clear
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-slate-300 rounded-sm cursor-pointer hover:border-[#F87217] hover:bg-orange-50/30 transition-colors"
                    onClick={() => dbFileInputRef.current?.click()}
                  >
                    <input type="file" ref={dbFileInputRef} onChange={handleDbFileUpload} accept=".db,.sqlite,.sqlite3" className="hidden" />
                    {dbUploading ? <Loader2 className="w-8 h-8 animate-spin text-[#F87217]" /> : <Database className="w-8 h-8 text-slate-400" />}
                    <p className="text-sm font-medium text-slate-600">{dbUploading ? 'Uploading...' : 'Upload a SQLite database to get started'}</p>
                    <p className="text-xs text-slate-400">Accepts .db, .sqlite, .sqlite3 files</p>
                  </div>
                )}
              </div>
            )}

            {/* Messages — flex-1, scrollable. ONLY this area scrolls. */}
            <div
              className="flex-1 py-6 overflow-y-auto min-h-0"
              style={{ background: isDark ? '#0a0f1e' : '#ffffff' }}
            >
              <div className="w-full px-2 sm:px-4">
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center text-center pt-16 pb-12">
                    {/* Animated glow ring */}
                    <div className="relative mb-6">
                      <div
                        className="w-20 h-20 rounded-full flex items-center justify-center"
                        style={{
                          background: 'linear-gradient(135deg, rgba(255,79,0,0.12) 0%, rgba(248,114,23,0.08) 100%)',
                          boxShadow: '0 0 0 8px rgba(248,114,23,0.06), 0 0 0 16px rgba(248,114,23,0.03)',
                        }}
                      >
                        <div
                          className="w-14 h-14 rounded-full bg-gradient-to-br from-[#FF4F00] to-[#F87217] flex items-center justify-center shadow-lg"
                          style={{ boxShadow: '0 4px 20px rgba(248,114,23,0.35)' }}
                        >
                          <span className="text-white">{getIcon(agent.icon)}</span>
                        </div>
                      </div>
                      <span
                        className="absolute top-1 right-1 w-4 h-4 rounded-full bg-emerald-400 border-2 border-white shadow"
                        style={{ boxShadow: '0 0 8px rgba(52,211,153,0.6)' }}
                      />
                    </div>
                    <h3 className="text-lg font-bold mb-1.5" style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}>
                      {isDbAgent ? 'Query your database' : 'Start a conversation'}
                    </h3>
                    <p className="text-sm text-slate-400 max-w-xs leading-relaxed">
                      {isDbAgent
                        ? (dbStatus.uploaded
                          ? `Your database "${dbStatus.db_name}" is ready. Ask questions in plain English.`
                          : 'Upload a .db or .sqlite file above, then ask questions about your data.')
                        : <>Ask me anything about <span className="text-[#F87217] font-medium">{agent.category.toLowerCase()}</span>.</>
                      }
                    </p>
                    {!isDbAgent && (
                      <div className="mt-5 flex flex-wrap justify-center gap-2">
                        {['What can you do?', 'Get started', 'Show me an example'].map(prompt => (
                          <button
                            key={prompt}
                            onClick={() => setInputMessage(prompt)}
                            className={`px-4 py-1.5 rounded-full text-xs font-medium border shadow-sm transition-all duration-200 ${isDark ? 'bg-[#0f1729] border-[#1e2d52] text-[#c9d1d9] hover:border-[#F87217] hover:text-[#F87217]' : 'bg-white border-slate-200 text-slate-500 hover:border-[#F87217] hover:text-[#F87217] hover:bg-orange-50/50'}`}
                          >
                            {prompt}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <MemoizedMessageList
                    messages={messages}
                    sending={sending}
                    streamEvents={streamEvents}
                    parsePptDownloadToken={parsePptDownloadToken}
                    downloadGeneratedPpt={downloadGeneratedPpt}
                    downloadGeneratedDoc={downloadGeneratedDoc}
                    downloadGeneratedZip={downloadGeneratedZip}
                    docViewerDataMap={docViewerDataMap}
                    docViewerFileId={docViewerFileId}
                    setDocViewerFileId={setDocViewerFileId}
                    setDocViewerFilename={setDocViewerFilename}
                    setDocViewerOpen={setDocViewerOpen}
                    setDocViewerLoading={setDocViewerLoading}
                    setDocViewerDataMap={setDocViewerDataMap}
                    token={token}
                    messagesEndRef={messagesEndRef}
                    isDark={isDark}
                    onImageClick={setExpandedImage}
                  />
                )}
              </div>
            </div>

            {/* Input bar — pinned to bottom, collapsible */}
            <div className="flex-shrink-0" style={{ background: isDark ? '#0a0f1e' : '#ffffff' }}>
              {/* Collapse toggle strip */}
              <button
                onClick={() => setInputBarCollapsed(v => !v)}
                className={`w-full flex items-center justify-center py-1 border-t transition-colors ${
                  isDark ? 'border-[#1e2d52] hover:bg-white/5' : 'border-slate-200 hover:bg-slate-50'
                }`}
                title={inputBarCollapsed ? 'Expand input' : 'Collapse input'}
              >
                {inputBarCollapsed
                  ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" />
                  : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
              </button>

              {/* Actual input content */}
              <div
                className={`px-4 pb-4 pt-2 border-t ${isDark ? 'border-[#1e2d52]' : 'border-slate-200'}`}
                style={{ background: isDark ? '#0a0f1e' : '#ffffff', display: inputBarCollapsed ? 'none' : undefined }}
              >
                <div className="mx-auto w-full max-w-2xl">
                {isAuthenticated ? (
                  TIER_HIERARCHY[(user?.plan || 'free').toLowerCase()] < TIER_HIERARCHY[(agent?.tier || 'free').toLowerCase()] &&
                    !(agent?.is_custom && agent?.id === user?.id) ? (
                    <div className={`p-8 border-2 border-dashed rounded-lg text-center ${isDark ? 'bg-[#0f1729] border-[#1e2d52]' : 'bg-slate-50 border-slate-200'}`}>
                      <Zap className="w-10 h-10 text-[#F87217] mx-auto mb-4" />
                      <h3 className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        Upgrade to {(agent?.tier || 'Pro').toUpperCase()} Required
                      </h3>
                      <p className={`text-sm mb-6 max-w-md mx-auto ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                        This specialized AI agent is exclusive to {(agent?.tier || 'Pro')} tier members.
                        Unlock access to this and 30+ other premium agents by upgrading your plan.
                      </p>
                      <Button
                        onClick={() => navigate('/pricing')}
                        className="bg-[#F87217] hover:bg-[#C65B12] text-white px-8 h-11 rounded-sm shadow-lg shadow-orange-200"
                      >
                        Upgrade Now
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {isRecording && (
                        <div className="flex items-center justify-center gap-3 p-3 bg-red-50 border border-red-200 rounded-sm">
                          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                          <span className="text-red-600 font-medium">Recording: {formatRecordingTime(recordingTime)}</span>
                          <Button size="sm" onClick={stopRecording} className="bg-red-500 hover:bg-red-600 text-white" data-testid="stop-recording-btn">
                            <Square className="w-4 h-4 mr-1" />Stop
                          </Button>
                        </div>
                      )}
                      {transcribing && (
                        <div className="flex items-center justify-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-sm">
                          <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                          <span className="text-blue-600 font-medium">Transcribing and processing...</span>
                        </div>
                      )}
                      {(uploadedFiles.length > 0 || selectedDriveFiles.length > 0) && (
                        <div className="flex flex-wrap gap-2">
                          {uploadedFiles.map((file) => (
                            <div key={file.id} className={`flex items-center gap-2 px-3 py-2 border rounded-sm ${isDark ? 'bg-[#0f1729] border-[#1e2d52] text-[#c9d1d9]' : 'bg-slate-100 border-slate-200 text-slate-700'}`}>
                              {getFileIcon(file.original_filename)}
                              <span className="text-sm max-w-[150px] truncate">{file.original_filename}</span>
                              <button onClick={() => removeFile(file.id)} className="text-slate-400 hover:text-red-500">
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                          {selectedDriveFiles.map((file) => (
                            <div key={`drive-${file.id}`} className={`flex items-center gap-2 px-3 py-2 border rounded-sm ${isDark ? 'bg-[#0a0f1e] border-blue-800 text-blue-300' : 'bg-blue-50 border-blue-200 text-blue-700'}`}>
                              <img src="https://upload.wikimedia.org/wikipedia/commons/1/12/Google_Drive_icon_%282020%29.svg" alt="" className="w-4 h-4" />
                              <span className="text-sm max-w-[150px] truncate">{file.name}</span>
                              <button onClick={() => setSelectedDriveFiles(prev => prev.filter(f => f.id !== file.id))} className="text-blue-400 hover:text-red-500">
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Tool Chips Row */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {[
                          { key: 'web', label: 'Web Search', icon: Search, enabled: webSearchEnabled, toggle: () => setWebSearchEnabled(v => !v) },
                        ].map(({ key, label, icon: Icon, enabled, toggle }) => (
                          <button
                            key={key}
                            type="button"
                            onClick={toggle}
                            disabled={sending || isRecording || transcribing}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${enabled
                              ? 'bg-[#F87217] border-[#F87217] text-white shadow-sm shadow-orange-200'
                              : isDark ? 'bg-[#0f1729] border-[#1e2d52] text-[#c9d1d9] hover:border-[#F87217] hover:text-[#F87217]' : 'bg-white border-slate-200 text-slate-600 hover:border-[#F87217] hover:text-[#F87217]'
                              }`}
                            title={enabled ? `Disable ${label}` : `Enable ${label}`}
                          >
                            <Icon className="w-3.5 h-3.5" />
                            {label}
                            {enabled && (
                              <span className="ml-0.5 w-1.5 h-1.5 rounded-full bg-white opacity-80 inline-block" />
                            )}
                          </button>
                        ))}
                      </div>

                      {/* ── ASDP Quick-Action Buttons ──────────────────────── */}
                      {isAsdp && (
                        <div className="flex flex-wrap gap-2">
                          {/* Approve */}
                          <button
                            type="button"
                            onClick={() => sendAsdpCommand('approve')}
                            disabled={sending}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                            title="Approve current stage and proceed"
                          >
                            <Check className="w-3.5 h-3.5" /> Approve
                          </button>

                          {/* Reject */}
                          {asdpRejectOpen ? (
                            <div className="flex items-center gap-1.5">
                              <input
                                autoFocus
                                value={asdpRejectFeedback}
                                onChange={e => setAsdpRejectFeedback(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') { sendAsdpCommand(`reject ${asdpRejectFeedback}`); setAsdpRejectOpen(false); setAsdpRejectFeedback(''); } if (e.key === 'Escape') { setAsdpRejectOpen(false); setAsdpRejectFeedback(''); } }}
                                placeholder="Reason (optional)…"
                                className="text-xs border border-red-300 rounded-full px-3 py-1.5 outline-none focus:ring-1 focus:ring-red-400 w-44 text-slate-900 bg-white"
                              />
                              <button
                                type="button"
                                onClick={() => { sendAsdpCommand(`reject ${asdpRejectFeedback}`.trim()); setAsdpRejectOpen(false); setAsdpRejectFeedback(''); }}
                                className="px-2.5 py-1.5 rounded-full text-xs font-semibold bg-red-500 text-white hover:bg-red-600"
                              >Send</button>
                              <button type="button" onClick={() => { setAsdpRejectOpen(false); setAsdpRejectFeedback(''); }} className="text-slate-400 hover:text-slate-600"><X className="w-3.5 h-3.5" /></button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setAsdpRejectOpen(true)}
                              disabled={sending}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all bg-red-50 border-red-300 text-red-700 hover:bg-red-100 disabled:opacity-50"
                              title="Reject current stage"
                            >
                              <X className="w-3.5 h-3.5" /> Reject
                            </button>
                          )}

                          {/* Rerun */}
                          {asdpRerunOpen ? (
                            <div className="flex items-center gap-1.5">
                              <input
                                autoFocus
                                value={asdpRerunFeedback}
                                onChange={e => setAsdpRerunFeedback(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter' && asdpRerunFeedback.trim()) { sendAsdpCommand(`rerun ${asdpRerunFeedback}`); setAsdpRerunOpen(false); setAsdpRerunFeedback(''); } if (e.key === 'Escape') { setAsdpRerunOpen(false); setAsdpRerunFeedback(''); } }}
                                placeholder="Feedback for rerun…"
                                className="text-xs border border-amber-300 rounded-full px-3 py-1.5 outline-none focus:ring-1 focus:ring-amber-400 w-52 text-slate-900 bg-white"
                              />
                              <button
                                type="button"
                                onClick={() => { if (asdpRerunFeedback.trim()) { sendAsdpCommand(`rerun ${asdpRerunFeedback}`); setAsdpRerunOpen(false); setAsdpRerunFeedback(''); } }}
                                disabled={!asdpRerunFeedback.trim()}
                                className="px-2.5 py-1.5 rounded-full text-xs font-semibold bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50"
                              >Send</button>
                              <button type="button" onClick={() => { setAsdpRerunOpen(false); setAsdpRerunFeedback(''); }} className="text-slate-400 hover:text-slate-600"><X className="w-3.5 h-3.5" /></button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setAsdpRerunOpen(true)}
                              disabled={sending}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                              title="Rerun current stage with feedback"
                            >
                              <RotateCcw className="w-3.5 h-3.5" /> Rerun
                            </button>
                          )}

                          {/* Status */}
                          <button
                            type="button"
                            onClick={() => sendAsdpCommand('status')}
                            disabled={sending}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                            title="Show pipeline status"
                          >
                            <BarChart3 className="w-3.5 h-3.5" /> Status
                          </button>

                          {/* Artifacts — dropdown of all 8 pipeline stages */}
                          <div className="relative">
                            {asdpArtifactsOpen && (
                              <div className="absolute bottom-full mb-2 left-0 z-50 bg-white border border-teal-200 rounded-xl shadow-xl p-2 min-w-[210px]">
                                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide px-2 mb-1.5">Select Stage</p>
                                {ASDP_STAGES.map(s => (
                                  <button
                                    key={s.id}
                                    type="button"
                                    onClick={() => { sendAsdpCommand(`artifacts ${s.id}`); setAsdpArtifactsOpen(false); }}
                                    className="w-full text-left px-3 py-1.5 text-xs text-slate-700 hover:bg-teal-50 hover:text-teal-700 rounded-lg transition-colors flex items-center gap-2"
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full bg-teal-400 flex-shrink-0" />
                                    {s.label}
                                  </button>
                                ))}
                                <div className="border-t border-slate-100 mt-1 pt-1">
                                  <button type="button" onClick={() => setAsdpArtifactsOpen(false)} className="w-full text-left px-3 py-1.5 text-xs text-slate-400 hover:text-slate-600 rounded-lg transition-colors flex items-center gap-2">
                                    <X className="w-3 h-3" /> Cancel
                                  </button>
                                </div>
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() => setAsdpArtifactsOpen(v => !v)}
                              disabled={sending}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${asdpArtifactsOpen ? 'bg-teal-100 border-teal-400 text-teal-800' : 'bg-teal-50 border-teal-300 text-teal-700 hover:bg-teal-100'} disabled:opacity-50`}
                              title="View artifacts for a pipeline stage"
                            >
                              <FolderOpen className="w-3.5 h-3.5" /> Artifacts
                            </button>
                          </div>

                          {/* Download */}
                          <button
                            type="button"
                            onClick={() => sendAsdpCommand('download')}
                            disabled={sending}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100 disabled:opacity-50"
                            title="Download all generated code as .zip"
                          >
                            <Download className="w-3.5 h-3.5" /> Download
                          </button>

                          {/* Reset */}
                          <button
                            type="button"
                            onClick={() => sendAsdpCommand('reset')}
                            disabled={sending}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all bg-slate-100 border-slate-300 text-slate-600 hover:bg-slate-200 disabled:opacity-50"
                            title="Reset pipeline and start fresh"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Reset
                          </button>

                          {/* Help */}
                          <button
                            type="button"
                            onClick={() => sendAsdpCommand('help')}
                            disabled={sending}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100 disabled:opacity-50"
                            title="Show help and all commands"
                          >
                            <Sparkles className="w-3.5 h-3.5" /> Help
                          </button>
                        </div>
                      )}

                      <div className={`relative flex items-end w-full rounded-[26px] p-1.5 transition-all shadow-sm border ${isDark ? 'bg-slate-800 border-transparent focus-within:ring-1 focus-within:ring-slate-500' : 'bg-[#f4f4f4] border-transparent focus-within:bg-white focus-within:ring-1 focus-within:ring-slate-300'}`}>
                        {/* Hidden file inputs */}
                        <input type="file" ref={fileInputRef} onChange={handleFileSelect} multiple accept={ALLOWED_EXTENSIONS.join(',')} className="hidden" data-testid="file-input" />
                        <input type="file" ref={photoInputRef} onChange={handleFileSelect} multiple accept="image/*" className="hidden" data-testid="photo-input" />
                        <input type="file" ref={cameraInputRef} onChange={handleFileSelect} accept="image/*" capture="environment" className="hidden" data-testid="camera-input" />

                        {/* Left Action Group */}
                        <div className="flex shrink-0 items-center mb-1">

                          {/* Connectors Button — hidden when agent has no allowed connectors */}
                          {allowedConnectorKeys.length > 0 && (
                            <div className="relative" ref={connectorsMenuRef}>
                              <button
                                type="button"
                                onClick={() => setShowConnectorsMenu(v => !v)}
                                disabled={sending || isRecording || transcribing}
                                title="Connect apps"
                                className={`w-9 h-9 rounded-full flex shrink-0 items-center justify-center transition-all ${showConnectorsMenu || Object.values(connectedApps).some(Boolean)
                                  ? 'bg-blue-100 text-blue-600'
                                  : isDark ? 'text-slate-400 hover:bg-[#404040] hover:text-slate-200' : 'text-slate-500 hover:bg-[#e4e4e4] hover:text-slate-700'
                                  }`}
                              >
                                <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                  <rect x="2" y="2" width="6" height="6" rx="1.5" />
                                  <rect x="12" y="2" width="6" height="6" rx="1.5" />
                                  <rect x="2" y="12" width="6" height="6" rx="1.5" />
                                  <rect x="12" y="12" width="6" height="6" rx="1.5" />
                                </svg>
                                {/* Label intentionally removed for compact design */}
                                {Object.values(connectedApps).some(Boolean) && (
                                  <span className="absolute top-0 right-0 w-2 h-2 rounded-full bg-blue-500 border border-white inline-block" />
                                )}
                              </button>

                              {showConnectorsMenu && allowedConnectorKeys.length > 0 && (
                                <div className="absolute bottom-14 left-0 z-50 min-w-[320px] rounded-xl shadow-xl overflow-hidden" style={{ background: isDark ? '#0f1729' : '#fff', border: `1px solid ${isDark ? '#1e2d52' : '#e2e8f0'}` }}>
                                  <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2" style={{ borderColor: isDark ? '#1e2d52' : '#f1f5f9' }}>
                                    <svg viewBox="0 0 20 20" fill="currentColor" className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                      <rect x="2" y="2" width="6" height="6" rx="1.5" />
                                      <rect x="12" y="2" width="6" height="6" rx="1.5" />
                                      <rect x="2" y="12" width="6" height="6" rx="1.5" />
                                      <rect x="12" y="12" width="6" height="6" rx="1.5" />
                                    </svg>
                                    <p className={`text-xs font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Add connectors</p>
                                  </div>
                                  <div className="py-1.5 px-1">
                                    {[
                                      {
                                        key: 'drive', label: 'Google Drive', subtitle: 'Access your files & docs',
                                        logo: (<img src="https://upload.wikimedia.org/wikipedia/commons/1/12/Google_Drive_icon_%282020%29.svg" alt="Google Drive" className="w-5 h-5" />)
                                      },
                                      {
                                        key: 'calendar', label: 'Google Calendar', subtitle: 'Schedule & manage events',
                                        logo: (<img src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg" alt="Google Calendar" className="w-5 h-5" />)
                                      },
                                      {
                                        key: 'github', label: 'GitHub', subtitle: 'Repos, issues & pull requests',
                                        logo: (<svg className="w-5 h-5" viewBox="0 0 98 96" fill="currentColor"><path fillRule="evenodd" clipRule="evenodd" d="M48.854 0C21.839 0 0 22 0 49.217c0 21.756 13.993 40.172 33.405 46.69 2.427.49 3.316-1.059 3.316-2.362 0-1.141-.08-5.052-.08-9.127-13.59 2.934-16.42-5.867-16.42-5.867-2.184-5.704-5.42-7.17-5.42-7.17-4.448-3.015.324-3.015.324-3.015 4.934.326 7.523 5.052 7.523 5.052 4.367 7.496 11.404 5.378 14.235 4.074.404-3.178 1.699-5.378 3.074-6.6-10.839-1.141-22.243-5.378-22.243-24.283 0-5.378 1.94-9.778 5.014-13.2-.485-1.222-2.184-6.275.486-13.038 0 0 4.125-1.304 13.426 5.052a46.97 46.97 0 0 1 12.214-1.63c4.125 0 8.33.571 12.213 1.63 9.302-6.356 13.427-5.052 13.427-5.052 2.67 6.763.97 11.816.485 13.038 3.155 3.422 5.015 7.822 5.015 13.2 0 18.905-11.404 23.06-22.324 24.283 1.78 1.548 3.316 4.481 3.316 9.126 0 6.6-.08 11.897-.08 13.526 0 1.304.89 2.853 3.316 2.364 19.412-6.52 33.405-24.935 33.405-46.691C97.707 22 75.788 0 48.854 0z" /></svg>)
                                      },
                                    ].map(({ key, label, subtitle, logo }) => (
                                      <div key={key} className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors"
                                        style={{ color: isDark ? '#c9d1d9' : 'inherit' }}
                                        onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc'; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                      >
                                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-[#0f1729]' : 'bg-slate-50 border border-slate-100'}`}>
                                          {logo}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className={`text-sm font-medium whitespace-nowrap ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{label}</p>
                                          <p className="text-xs text-slate-400 truncate">{subtitle}</p>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => toggleConnector(key, label)}
                                          disabled={connectingApp === key}
                                          className={`flex-shrink-0 min-w-[72px] text-center py-1 rounded-full text-xs font-semibold transition-all border ${connectedApps[key]
                                            ? (isDark ? 'bg-blue-500/10 border-blue-500/20 text-blue-400 hover:bg-red-500/10 hover:border-red-500/20 hover:text-red-400' : 'bg-blue-50 border-blue-200 text-blue-600 hover:bg-red-50 hover:border-red-200 hover:text-red-500')
                                            : connectingApp === key
                                              ? (isDark ? 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400 cursor-wait' : 'bg-yellow-50 border-yellow-300 text-yellow-600 cursor-wait')
                                              : (isDark ? 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-blue-500/10 hover:border-blue-500/30 hover:text-blue-400' : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600')
                                            }`}
                                        >
                                          {connectedApps[key] ? 'Connected' : connectingApp === key ? 'Connecting…' : 'Connect'}
                                        </button>
                                        {/* Drive file picker button */}
                                        {key === 'drive' && connectedApps.drive && (
                                          <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); setShowDrivePicker(v => !v); }}
                                            className={`flex-shrink-0 px-2 py-1 rounded-full text-xs font-medium border transition-colors ${
                                              isDark
                                                ? 'border-blue-500/30 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20'
                                                : 'border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100'
                                            }`}
                                          >
                                            Browse
                                          </button>
                                        )}
                                      </div>
                                    ))}
                                  </div>

                                  {/* Google Drive File Picker */}
                                  {showDrivePicker && connectedApps.drive && (
                                    <div className="border-t" style={{ borderColor: isDark ? '#1e2d52' : '#f1f5f9' }}>
                                      <div className="px-3 py-2">
                                        <input
                                          type="text"
                                          placeholder="Search documents..."
                                          value={driveSearch}
                                          onChange={(e) => setDriveSearch(e.target.value)}
                                          className={`w-full px-2.5 py-1.5 text-xs rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400 transition-shadow ${isDark ? 'bg-[#0a0f1e] border-[#1e2d52] text-slate-200 placeholder:text-slate-500' : 'border border-slate-200'}`}
                                        />
                                      </div>
                                      <div className="max-h-48 overflow-y-auto px-1 pb-2">
                                        {driveLoading ? (
                                          <div className="flex items-center justify-center py-4">
                                            <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                                            <span className="ml-2 text-xs text-slate-400">Loading files...</span>
                                          </div>
                                        ) : driveFiles.length === 0 ? (
                                          <p className="text-xs text-slate-400 text-center py-4">No files found</p>
                                        ) : (
                                          driveFiles.map(file => {
                                            const isSelected = selectedDriveFiles.some(f => f.id === file.id);
                                            const mimeShort = (file.mime_type || '').split('/').pop().split('.').pop();
                                            return (
                                              <div
                                                key={file.id}
                                                onClick={() => toggleDriveFile(file)}
                                                className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors ${isSelected ? 'bg-blue-50 border border-blue-200' : ''}`}
                                                style={isSelected ? {} : {}}
                                                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc'; }}
                                                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                                              >
                                                <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-[#0f1729]' : 'bg-slate-100'}`}>
                                                  {getDriveFileIcon(file.mime_type)}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                  <p className={`text-xs font-medium truncate ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{file.name}</p>
                                                  <p className="text-[10px] text-slate-400">{mimeShort}</p>
                                                </div>
                                                {isSelected && <Check className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />}
                                              </div>
                                            );
                                          })
                                        )}
                                      </div>
                                      {selectedDriveFiles.length > 0 && (
                                        <div className="px-3 py-2 border-t border-slate-100">
                                          <p className="text-[10px] text-blue-600 font-medium">{selectedDriveFiles.length} file(s) selected — will be sent as context</p>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  <div className="px-4 py-2 border-t border-slate-100">
                                    <p className="text-[10px] text-slate-400">Connected apps can access your chat context</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Attach menu */}
                          <div className="relative" ref={attachMenuRef}>
                            <button
                              type="button"
                              onClick={() => setShowAttachMenu(v => !v)}
                              disabled={uploading || sending || isRecording || transcribing}
                              className={`w-9 h-9 rounded-full flex shrink-0 items-center justify-center transition-all ${showAttachMenu
                                ? 'bg-[#F87217]/10 text-[#F87217]'
                                : isDark ? 'text-slate-400 hover:bg-[#404040] hover:text-slate-200' : 'text-slate-500 hover:bg-[#e4e4e4] hover:text-slate-700'
                                }`}
                              data-testid="attach-menu-btn" title="Attach"
                            >
                              {uploading
                                ? <Loader2 className="w-5 h-5 animate-spin" />
                                : <Plus className="w-5 h-5" />}
                            </button>

                              {showAttachMenu && (
                                <div className="absolute bottom-14 left-0 z-50 w-52 rounded-xl shadow-xl overflow-hidden"
                                  style={{ background: isDark ? '#0f1729' : '#fff', border: `1px solid ${isDark ? '#1e2d52' : '#e2e8f0'}` }}>
                                  <div className="px-3 py-2" style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9'}` }}>
                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Attach</p>
                                  </div>
                                  <div className="py-1">
                                    <button
                                      type="button"
                                      onClick={() => { setShowAttachMenu(false); fileInputRef.current?.click(); }}
                                      className="w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors"
                                      style={{ color: isDark ? '#c9d1d9' : '#374151' }}
                                      onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc'; }}
                                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                    >
                                      <span className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                                        <Paperclip className="w-4 h-4 text-blue-600" />
                                      </span>
                                      <div className="text-left">
                                        <p className="font-medium" style={{ color: isDark ? '#e6edf3' : '#1e293b' }}>Add Files</p>
                                        <p className="text-xs text-slate-400">PDF, CSV, DOCX &amp; more</p>
                                      </div>
                                    </button>
                                  </div>
                                </div>
                              )}
                          </div>

                          {/* KB Dropdown */}
                          <div className="relative" ref={kbDropdownRef}>
                            <button
                              type="button"
                              onClick={() => setShowKbDropdown(v => !v)}
                              disabled={sending || isRecording || transcribing}
                              className={`w-9 h-9 rounded-full flex shrink-0 items-center justify-center transition-all ${kbConnected || showKbDropdown
                                ? 'bg-[#FF4F00]/10 text-[#FF4F00]'
                                : isDark ? 'text-slate-400 hover:bg-[#404040] hover:text-slate-200' : 'text-slate-500 hover:bg-[#e4e4e4] hover:text-slate-700'
                                }`}
                              title={kbConnected ? `KB connected: ${kbCategory}` : 'Connect to Knowledge Base'}
                            >
                              <Database className="w-5 h-5" />
                            </button>
                            {showKbDropdown && (
                              <div className="absolute bottom-14 left-0 z-50 w-64 rounded-sm shadow-lg" style={{ background: isDark ? '#0f1729' : '#fff', border: `1px solid ${isDark ? '#1e2d52' : '#e2e8f0'}` }}>
                                <div className="px-3 py-2" style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9'}` }}>
                                  <p className="text-xs font-semibold" style={{ color: isDark ? '#e6edf3' : '#334155' }}>Connect to Knowledge Base</p>
                                  <p className="text-xs text-slate-400 mt-0.5">Select a category to ground this conversation</p>
                                </div>
                                <div className="py-1 max-h-52 overflow-y-auto">
                                  {kbCategories.length === 0 ? (
                                    <div className="px-3 py-4 text-center">
                                      <p className="text-xs text-slate-500">No knowledge base files yet</p>
                                      <a href="/knowledge-base" className="text-xs text-[#FF4F00] underline mt-1 inline-block" onClick={() => setShowKbDropdown(false)}>
                                        Manage Knowledge Base →
                                      </a>
                                    </div>
                                  ) : (
                                    kbCategories.map(cat => (
                                      <button
                                        key={cat.name}
                                        onClick={() => { setKbCategory(cat.name); setKbConnected(true); setShowKbDropdown(false); toast.success(`Knowledge Base connected: ${cat.name}`); }}
                                        className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between transition-colors ${kbConnected && kbCategory === cat.name ? 'bg-[#FF4F00]/5 text-[#FF4F00]' : ''}`}
                                        style={{ color: kbConnected && kbCategory === cat.name ? '#FF4F00' : (isDark ? '#c9d1d9' : '#374151') }}
                                        onMouseEnter={e => { if (!(kbConnected && kbCategory === cat.name)) e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc'; }}
                                        onMouseLeave={e => { if (!(kbConnected && kbCategory === cat.name)) e.currentTarget.style.background = kbConnected && kbCategory === cat.name ? 'rgba(255,79,0,0.05)' : 'transparent'; }}
                                      >
                                        <span className="font-medium">{cat.name}</span>
                                        <span className="text-xs text-slate-400">{cat.file_count} {cat.file_count === 1 ? 'file' : 'files'}</span>
                                      </button>
                                    ))
                                  )}
                                </div>
                                {kbConnected && (
                                  <div className="px-3 py-2 border-t border-slate-100">
                                    <button
                                      onClick={() => { setKbConnected(false); setKbCategory(''); setShowKbDropdown(false); toast.success('Knowledge Base disconnected'); }}
                                      className="text-xs text-red-400 hover:text-red-600 transition-colors w-full text-left"
                                    >
                                      × Disconnect Knowledge Base
                                    </button>
                                  </div>
                                )}
                                <div className="px-3 py-1.5 border-t border-slate-100 flex items-center justify-between">
                                  <a href="/knowledge-base" className="text-xs text-slate-400 hover:text-[#FF4F00] transition-colors" onClick={() => setShowKbDropdown(false)}>
                                    Manage Knowledge Base →
                                  </a>
                                  {connectedApps.drive && (
                                    <button
                                      type="button"
                                      onClick={() => setShowKbDrivePicker(v => !v)}
                                      className="text-xs text-blue-500 hover:text-blue-700 transition-colors flex items-center gap-1"
                                    >
                                      <img src="https://upload.wikimedia.org/wikipedia/commons/1/12/Google_Drive_icon_%282020%29.svg" alt="" className="w-3 h-3" />
                                      From Drive
                                    </button>
                                  )}
                                </div>

                                {/* KB Drive Import Picker */}
                                {showKbDrivePicker && connectedApps.drive && (
                                  <div className="border-t border-blue-100 bg-blue-50/30">
                                    <div className="px-3 py-2">
                                      <input
                                        type="text"
                                        placeholder="Search Drive files..."
                                        value={kbDriveSearch}
                                        onChange={(e) => setKbDriveSearch(e.target.value)}
                                        className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:border-blue-400"
                                      />
                                    </div>
                                    <div className="max-h-40 overflow-y-auto px-1 pb-2">
                                      {kbDriveLoading ? (
                                        <div className="flex items-center justify-center py-3">
                                          <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                                          <span className="ml-2 text-xs text-slate-400">Loading...</span>
                                        </div>
                                      ) : kbDriveFiles.length === 0 ? (
                                        <p className="text-xs text-slate-400 text-center py-3">No files found</p>
                                      ) : (
                                        kbDriveFiles.map(file => (
                                          <div
                                            key={file.id}
                                            className="flex items-center gap-2 px-3 py-2 rounded-md transition-colors"
                                            onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : '#f8fafc'; }}
                                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                          >
                                            <div className="w-5 h-5 rounded bg-white flex items-center justify-center flex-shrink-0">
                                              {getDriveFileIcon(file.mime_type)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <p className="text-xs font-medium text-slate-700 truncate">{file.name}</p>
                                            </div>
                                            <button
                                              type="button"
                                              onClick={() => importDriveFileToKb(file)}
                                              disabled={kbDriveImporting === file.id}
                                              className="flex-shrink-0 px-2 py-0.5 rounded text-[10px] font-medium border border-blue-200 bg-white text-blue-600 hover:bg-blue-50 disabled:opacity-50 transition-colors"
                                            >
                                              {kbDriveImporting === file.id ? 'Importing…' : 'Import'}
                                            </button>
                                          </div>
                                        ))
                                      )}
                                    </div>
                                    {!kbConnected && (
                                      <div className="px-3 py-1.5 border-t border-blue-100">
                                        <p className="text-[10px] text-amber-600">Select a KB category above first, then import</p>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div> {/* End Left Action Group */}

                        <Textarea
                          ref={chatInputRef}
                          value={inputMessage}
                          onChange={(e) => setInputMessage(e.target.value)}
                          onKeyDown={handleInputKeyDown}
                          placeholder={uploadedFiles.length > 0 ? 'Add a message about your files...' : `Message ${formatAgentName(agent.name)}`}
                          rows={1}
                          className={`flex-1 min-w-[50px] min-h-[44px] max-h-32 bg-transparent border-0 ring-0 focus:ring-0 resize-none py-2.5 px-2 text-sm transition-all ${isDark ? 'text-[#c9d1d9] placeholder-[#8b949e]' : 'text-slate-900 placeholder-[#767676]'}`}
                          disabled={sending || isRecording || transcribing}
                          data-testid="chat-input"
                        />

                        {/* Right Action Group */}
                        <div className="flex shrink-0 items-center justify-end gap-1 mb-1 w-[80px]">

                          {/* Voice Button */}
                          <button
                            type="button"
                            onClick={isRecording ? stopRecording : startRecording}
                            disabled={sending || transcribing || uploading}
                            className={`w-9 h-9 rounded-full flex shrink-0 items-center justify-center transition-all ${isRecording
                              ? 'bg-red-100 text-red-600 animate-pulse'
                              : isDark ? 'text-slate-400 hover:bg-[#404040] hover:text-slate-200' : 'text-slate-500 hover:bg-[#e4e4e4] hover:text-slate-700'
                              }`}
                            data-testid="voice-input-btn"
                            title={isRecording ? 'Stop recording' : 'Start voice recording'}
                          >
                            {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                          </button>

                          <Button
                            onClick={sendMessage}
                            disabled={sending || isRecording || transcribing || (!inputMessage.trim() && uploadedFiles.length === 0)}
                            className={`w-9 h-9 rounded-full flex shrink-0 items-center justify-center p-0 transition-all ${inputMessage.trim() || uploadedFiles.length > 0 ? 'bg-gradient-to-br from-[#FF4F00] to-[#F87217] text-white shadow-md hover:scale-105' : isDark ? 'bg-[#404040] text-slate-500' : 'bg-[#e4e4e4] text-slate-400'}`}
                            data-testid="send-message-btn"
                          >
                            <Send className="w-4 h-4" style={{ marginLeft: '-2px' }} />
                          </Button>
                        </div> {/* End Right Action Group */}
                      </div>

                      <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
                        {kbConnected && kbCategory && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#F87217]/10 text-[#F87217] rounded-sm font-medium border border-[#F87217]/20">
                            <Database className="w-3 h-3" />KB: {kbCategory}
                            <button onClick={() => { setKbConnected(false); setKbCategory(''); }} className="ml-1 hover:text-[#C65B12] transition-colors" title="Disconnect">
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        )}
                        {webSearchEnabled && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#F87217]/10 text-[#F87217] rounded-sm font-medium border border-[#F87217]/20">
                            <Search className="w-3 h-3" />Web Search on
                            <button onClick={() => setWebSearchEnabled(false)} className="ml-1 hover:text-[#C65B12]" title="Disable"><X className="w-3 h-3" /></button>
                          </span>
                        )}
                        <span><Plus className="w-3 h-3 inline mr-1" />Add photos, files, or take photo</span>
                        <span><Mic className="w-3 h-3 inline mr-1" />Voice input available</span>
                        <span>Enter to send, Shift+Enter for new line</span>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="text-center py-4">
                    <p className="text-slate-500 mb-3">Sign in to start chatting</p>
                    <Button onClick={() => navigate('/login')} className="bg-[#F87217] hover:bg-[#C65B12] text-white rounded-sm" data-testid="signin-to-chat">
                      Sign In
                    </Button>
                  </div>
                )}
              </div>
              </div>
            </div>
          </div>

          {/* Doc Viewer Side Panel — slides in from right over chat area */}
          {
            docViewerOpen && (
              <div className="w-[45vw] min-w-[400px] max-w-[700px] border-l border-slate-200 flex-shrink-0 flex flex-col">
                {docViewerLoading || !docViewerDataMap[docViewerFileId] ? (
                  <div className="h-full flex flex-col items-center justify-center gap-3 text-slate-500">
                    <Loader2 className="w-8 h-8 animate-spin" />
                    <p className="text-sm">Loading document...</p>
                  </div>
                ) : (
                  <DocViewerPanel
                    key={docViewerFileId}
                    docData={docViewerDataMap[docViewerFileId]}
                    fileId={docViewerFileId}
                    filename={docViewerFilename}
                    onClose={() => setDocViewerOpen(false)}
                    onDownload={downloadGeneratedDoc}
                    onRenameDownload={(fid, fname) => downloadGeneratedDoc(fid, fname, true)}
                    onSave={handleDocSave}
                    saving={docSaving}
                  />
                )}
              </div>
            )
          }


        </div >
      </div >

      {/* ── ASDP Wrong-Command Suggestion Modal ─────────────────────────── */}
      {
        asdpSuggestionModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm mx-4 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-base">Did you mean a command?</h3>
                  <p className="text-xs text-slate-500 mt-0.5">We detected a possible ASDP command typo</p>
                </div>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 mb-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">You typed:</span>
                  <code className="bg-red-50 text-red-700 px-2 py-0.5 rounded font-mono text-xs border border-red-100">{asdpSuggestionModal.typed}</code>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Did you mean:</span>
                  <code className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded font-mono text-xs border border-emerald-100 font-semibold">{asdpSuggestionModal.suggested}</code>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    // Replace first word with the suggested command and send directly
                    const words = inputMessage.trim().split(' ');
                    words[0] = asdpSuggestionModal.suggested;
                    const corrected = words.join(' ');
                    setAsdpSuggestionModal(null);
                    sendAsdpCommand(corrected);
                  }}
                  className="flex-1 py-2.5 rounded-xl bg-[#F87217] hover:bg-[#C65B12] text-white text-sm font-semibold transition-colors"
                >
                  Use "{asdpSuggestionModal.suggested}"
                </button>
                <button
                  onClick={() => {
                    setAsdpSuggestionModal(null);
                    // Resume original send
                    const origMsg = inputMessage;
                    const userMessage = { role: 'user', content: origMsg };
                    setMessages(prev => [...prev, userMessage]);
                    setInputMessage('');
                    setSending(true);
                    setStreamEvents([]);
                    axios.post(`${API}/chat`, {
                      agent_id: agentId,
                      message: origMsg,
                    }, { headers: { Authorization: `Bearer ${token}` } })
                      .then(res => {
                        const data = res.data?.data ?? res.data ?? {};
                        setMessages(prev => [...prev, { role: 'assistant', content: data.response || data.message || 'Done.' }]);
                        if (data.credits_used) updateCredits(data.credits_used);
                      })
                      .catch(err => {
                        toast.error(err.response?.data?.detail || 'Request failed');
                        setMessages(prev => [...prev, { role: 'assistant', content: '⚠️ Request failed. Please try again.' }]);
                      })
                      .finally(() => { setSending(false); setStreamEvents([]); });
                  }}
                  className="flex-1 py-2.5 rounded-xl border text-sm font-semibold transition-colors"
                  style={{ borderColor: isDark ? '#1e2d52' : '#e2e8f0', color: isDark ? '#c9d1d9' : '#475569' }}
                  onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  Send as-is
                </button>
              </div>
              <button
                onClick={() => setAsdpSuggestionModal(null)}
                className="w-full mt-2 py-2 text-xs text-slate-400 hover:text-slate-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )
      }

      {/* ── Fullscreen Wireframe Modal ───────────────────────────────────────── */}
      {expandedImage && (
        <Dialog open={!!expandedImage} onOpenChange={() => setExpandedImage(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] bg-slate-900 border-slate-700 p-0">
            <div className="relative w-full h-full flex items-center justify-center p-6">
              <img
                src={expandedImage}
                alt="Fullscreen wireframe"
                className="max-w-full max-h-full object-contain"
                style={{ maxHeight: '80vh' }}
              />
              <button
                onClick={() => setExpandedImage(null)}
                className="absolute top-4 right-4 p-2 rounded-full bg-slate-800 hover:bg-slate-700 text-white transition-colors"
                title="Close fullscreen"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div >
  );
};

export default AgentDetail;